import { useState, useEffect, useCallback, useMemo } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { API_BASE } from '../../lib/api-base';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ShoppingCart, Mail, CheckCircle2, AlertCircle, Zap, User as UserIcon, Sparkles, Trash2, RefreshCw, Plus, ExternalLink, FileText, Paperclip, Image as ImageIcon, PhoneCall, Truck, CreditCard, Bell, Wrench, Package, Ban, AlertTriangle, Move, MessageSquare, Send, Flag } from 'lucide-react';
import WhatIfPanel from '../../components/WhatIfPanel';
import AiSummaryPanel from '../../components/inquiry/AiSummaryPanel';
import StringDesignPanel from '../../components/inquiry/StringDesignPanel';
import LinkedTasksPanel from '../../components/LinkedTasksPanel';
import MailClassifierRulesPage from './MailClassifierRulesPage';


// 27.10.2026 (User-Wunsch): In der Anfrage ein Feld, um über die
// Auftragsnummer manuell mit einem Dashboard-Auftrag zu verknüpfen.
function InquiryOrderLink({ inquiry, onUpdated }) {
  const [num, setNum] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const linkedId = inquiry.linkedOrderId;
  const linkedNum = inquiry.linkedOrderNumber;

  const link = async () => {
    if (!num.trim()) return;
    setBusy(true); setErr('');
    const r = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/link-order`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderNumber: num.trim() }),
    });
    setBusy(false);
    if (r.ok) { setNum(''); onUpdated(await r.json()); }
    else { const d = await r.json().catch(() => ({})); setErr(d.detail || 'Verknüpfen fehlgeschlagen'); }
  };
  const unlink = async () => {
    setBusy(true); setErr('');
    const r = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/link-order`, { method: 'DELETE' });
    setBusy(false);
    if (r.ok) onUpdated(await r.json());
  };

  if (linkedId) {
    return (
      <div className="mt-2 flex items-center gap-2 flex-wrap text-xs" data-testid="inq-order-linked">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#9ec043]/10 text-[#5f7327] border border-[#9ec043]/30 font-semibold">
          <Package className="w-3 h-3" /> Verknüpft mit Auftrag {linkedNum || linkedId.slice(0, 8)}
        </span>
        <a href={`/dashboard/auftraege?open=${linkedId}`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1 text-blue-600 hover:underline" data-testid="inq-order-open">
          <ExternalLink className="w-3 h-3" /> Auftrag öffnen
        </a>
        <button onClick={unlink} disabled={busy}
          className="inline-flex items-center gap-1 text-red-600 hover:underline disabled:opacity-50"
          data-testid="inq-order-unlink-btn">
          <Ban className="w-3 h-3" /> Verknüpfung aufheben
        </button>
      </div>
    );
  }
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap text-xs" data-testid="inq-order-link-box">
      <span className="text-gray-500">Mit Auftrag verknüpfen:</span>
      <input value={num} onChange={(e) => setNum(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && link()}
        placeholder="Auftragsnummer (z. B. 11978)"
        className="w-48 border border-gray-200 rounded px-2 py-1 outline-none focus:border-[#9ec043]"
        data-testid="inq-order-link-input" />
      <button onClick={link} disabled={busy || !num.trim()}
        className="px-2 py-1 rounded bg-[#9ec043] text-white font-semibold hover:bg-[#8cb037] disabled:opacity-50"
        data-testid="inq-order-link-btn">Verknüpfen</button>
      {err && <span className="text-red-600" data-testid="inq-order-link-err">{err}</span>}
    </div>
  );
}

const TABS = [
  { id: 'product_inquiry',      label: 'Bestellanfragen',  icon: Zap },
  { id: 'order_status',         label: 'Auftrag / Lieferung', icon: Package },
  { id: 'installer_request',    label: 'Verbauer-Suche',   icon: Wrench },
  { id: 'support',              label: 'Support',          icon: AlertTriangle },
  { id: 'partner_lead',         label: 'Partner-Leads',    icon: UserIcon },
  { id: 'supplier',             label: 'Lieferanten',      icon: Truck },
  { id: 'payment_notification', label: 'Zahlung / Beleg',  icon: CreditCard },
  { id: 'notification',         label: 'Notifications',    icon: Bell },
  { id: 'spam',                 label: 'Spam',             icon: Ban },
  { id: 'general',              label: 'Allgemein',        icon: Mail },
];

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function InquiriesPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const isAdmin = user?.role === 'admin';
  // 04.02.2026: Matrix-basiertes Gating.
  //  - `inquiries.delete` → Lösch-Button (früher: `isAdmin || user.actions.includes()`
  //    — `user.actions` wurde aber nicht immer geladen, deshalb via `can()`).
  //  - `mail_inbound.admin` → Klassifikations-Regel-Editor (früher: hardcoded admin).
  const canDelete = can('inquiries.delete');
  const canEditRules = can('mail_inbound.admin');
  const [kind, setKind] = useState('product_inquiry');
  const [statusFilter, setStatusFilter] = useState('open');   // open | in_progress | closed | all — gilt für ALLE Kategorien
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInBody, setSearchInBody] = useState(false);  // Standard: nur Betreff + Absender
  const [showRules, setShowRules] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rematching, setRematching] = useState(null);
  const [rematchMsg, setRematchMsg] = useState(null);
  const [bulkIds, setBulkIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  // Bumpt sich, wenn der Innendienst CustomerData speichert — der
  // OfferDraftPanel re-fetcht dann sein Prefill, damit Modell-Wechsel,
  // Modul-Anzahl und GenLink-Position automatisch in den Angebotspositionen
  // erscheinen.
  const [prefillRefreshTick, setPrefillRefreshTick] = useState(0);

  const toggleBulk = (id) => {
    setBulkIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllBulk = (checked) => {
    if (!checked) return setBulkIds(new Set());
    setBulkIds(new Set(items.map(i => i.id)));
  };
  const bulkDelete = async () => {
    if (bulkIds.size === 0) return;
    if (!window.confirm(`${bulkIds.size} Anfragen / Angebote endgültig löschen?`)) return;
    setBulkDeleting(true);
    const res = await authFetch('/api/mail-inbound/inquiries/bulk-delete', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: Array.from(bulkIds) }),
    });
    setBulkDeleting(false);
    if (res.ok) {
      setBulkIds(new Set());
      if (selected && bulkIds.has(selected.id)) setSelected(null);
      load();
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    const res = await authFetch(`/api/mail-inbound/inquiries?kind=${kind}`);
    if (res.ok) setItems(await res.json());
    setLoading(false);
  }, [kind]);

  useEffect(() => { load(); setSelected(null); }, [load]);

  // Deep-Link Support: ?open=<inquiryId> in URL → richtige Kategorie
  // (kind) setzen und die Anfrage direkt im Detail-Panel öffnen. Wird z. B.
  // von der TasksPage genutzt, wenn der Klick auf den Source-Link einer
  // Anfrage-Aufgabe erfolgt. Wir holen die Anfrage einmal ab, lesen `kind`
  // aus der Antwort und setzen beides. Da der Kategorie-Wechsel den
  // `setSelected(null)`-Reset im `[load]`-Effekt auslöst, verzögern wir
  // unser `setSelected(...)` per Microtask — so gewinnt die explizite
  // Selektion garantiert. Der Query-Param wird danach entfernt, damit er
  // beim nächsten Reload nicht wieder triggert.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (!openId) return;
    let cancelled = false;
    (async () => {
      const r = await authFetch(`/api/mail-inbound/inquiries/${openId}`);
      if (!r.ok || cancelled) return;
      const inq = await r.json();
      if (inq.kind && inq.kind !== kind) setKind(inq.kind);
      // Verzögere die Selektion bis nach dem nächsten React-Render —
      // sonst überschreibt der `[load]`-Effekt unsere Auswahl.
      setTimeout(() => { if (!cancelled) setSelected(inq); }, 50);
      const url = new URL(window.location.href);
      url.searchParams.delete('open');
      window.history.replaceState({}, '', url.toString());
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredItems = useMemo(() => {
    // 1. Status-Filter — gilt jetzt für ALLE Kategorien, nicht nur Bestellanfragen.
    //    Damit kann der Innendienst auch Auftrag/Lieferung, Verbauer-Suche,
    //    Support etc. nach Bearbeitungsstand filtern.
    //    04.02.2026 (kritischer Bugfix!): `order_created` (Status nach
    //    „direktem Auftrag" via CHARGE/DELIVERY) war in KEINEM Filter-Tab
    //    enthalten — die Anfrage verschwand komplett aus der UI. Live-
    //    Vorfall bysolar/BV-Görlitz. Jetzt zählt `order_created` zu
    //    „In Bearbeitung", damit die Anfrage sichtbar bleibt bis sie
    //    manuell geschlossen wird.
    let list = items;
    if (statusFilter !== 'all') {
      const isClosed = (s) => s === 'closed' || s === 'done';
      const isInProgress = (s) => s === 'in_progress' || s === 'order_created';
      if (statusFilter === 'open')             list = list.filter(it => !it.status || it.status === 'new');
      else if (statusFilter === 'in_progress') list = list.filter(it => isInProgress(it.status));
      else if (statusFilter === 'closed')      list = list.filter(it => isClosed(it.status));
    }
    // 2. Such-Filter — Standard: Betreff + Absender (Name + E-Mail). Wenn
    //    `searchInBody` aktiv ist, wird zusätzlich der Nachrichteninhalt
    //    (bodyText) durchsucht. Case-insensitive Substring-Match.
    const q = (searchQuery || '').trim().toLowerCase();
    if (q) {
      list = list.filter(it => {
        const haystack = [
          it.subject || '',
          it.customerName || '',
          it.customerEmail || '',
        ];
        if (searchInBody) haystack.push(it.bodyText || '');
        return haystack.some(s => s.toLowerCase().includes(q));
      });
    }
    return list;
  }, [items, kind, statusFilter, searchQuery, searchInBody]);

  const counts = useMemo(() => {
    // Counts für ALLE Kategorien — Status-Tabs werden überall gezeigt.
    // 04.02.2026: `order_created` zählt zu „In Bearbeitung" (siehe filteredItems).
    return {
      open: items.filter(it => !it.status || it.status === 'new').length,
      in_progress: items.filter(it => it.status === 'in_progress' || it.status === 'order_created').length,
      closed: items.filter(it => it.status === 'closed' || it.status === 'done').length,
      all: items.length,
    };
  }, [items]);

  const setStatus = async (id, status) => {
    await authFetch(`/api/mail-inbound/inquiries/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    load();
    if (selected?.id === id) {
      const r = await authFetch(`/api/mail-inbound/inquiries/${id}`);
      if (r.ok) setSelected(await r.json());
    }
  };

  const remove = async (inq) => {
    if (!canDelete) return;
    if (!window.confirm(`Anfrage „${inq.subject}" unwiderruflich löschen?`)) return;
    const res = await authFetch(`/api/mail-inbound/inquiries/${inq.id}`, { method: 'DELETE' });
    if (res.ok) {
      if (selected?.id === inq.id) setSelected(null);
      load();
    } else {
      const d = await res.json().catch(() => ({}));
      alert(d.detail || 'Löschen fehlgeschlagen');
    }
  };

  const rematchCustomer = async (id) => {
    setRematching(id); setRematchMsg(null);
    const res = await authFetch(`/api/mail-inbound/inquiries/${id}/rematch-customer`, { method: 'POST' });
    setRematching(null);
    if (res.ok) {
      const d = await res.json();
      if (d.easybillCustomer) {
        setRematchMsg(`Kunde gefunden: ${d.easybillCustomer.company || (d.easybillCustomer.firstName + ' ' + d.easybillCustomer.lastName)} (#${d.easybillCustomer.number})`);
      } else {
        setRematchMsg('Immer noch kein Treffer — als Neukunde anlegen.');
      }
      const r = await authFetch(`/api/mail-inbound/inquiries/${id}`);
      if (r.ok) setSelected(await r.json());
    } else {
      const d = await res.json().catch(() => ({}));
      setRematchMsg(d.detail || 'Neusuche fehlgeschlagen');
    }
  };

  return (
    <div className="flex h-full" data-testid="inquiries-page">
      {showRules && canEditRules ? (
        <div className="flex-1 overflow-y-auto p-6">
          <MailClassifierRulesPage embedded onBack={() => setShowRules(false)} />
        </div>
      ) : (
      <>
      {/* Left: list — auf Handy/Tablet ausgeblendet, sobald ein Detail ausgewählt ist.
          04.02.2026: mobile responsive Fix — vorher blieben 380px Liste + Detail
          nebeneinander und wurden auf S24-Handy quer beide unlesbar. */}
      <div className={`${selected ? 'hidden lg:flex' : 'flex'} w-full lg:w-[380px] border-r border-gray-200 bg-white flex-col shrink-0`}>
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-lg font-bold text-[#32373c] flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-[#9ec043]" /> Anfragen / Angebote
              </h1>
              <p className="text-[11px] text-gray-400">Eingehende Mails automatisch klassifiziert</p>
            </div>
            {canEditRules && (
              <button onClick={() => setShowRules(true)}
                      className="text-[11px] px-2 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-[#9ec043] flex items-center gap-1"
                      data-testid="open-mail-rules">
                Regeln
              </button>
            )}
          </div>
          <div className="mt-3">
            <select value={kind} onChange={(e) => setKind(e.target.value)}
                    className="w-full px-2 py-1.5 text-[12px] rounded border border-gray-200 bg-white"
                    data-testid="inq-kind-select">
              {TABS.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
          {/* Suchfeld + Toggle „Auch Nachrichteninhalt durchsuchen" */}
          <div className="mt-2 space-y-1.5">
            <div className="relative">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Suche in Betreff & Absender …"
                className="h-7 text-[12px] pl-7 pr-7 bg-white border-gray-200"
                data-testid="inq-search-input"
              />
              <svg
                className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
                aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-[14px] leading-none px-1"
                  data-testid="inq-search-clear"
                  aria-label="Suche leeren">
                  ×
                </button>
              )}
            </div>
            <label className="flex items-center gap-1.5 text-[10px] text-gray-500 cursor-pointer select-none"
                   data-testid="inq-search-body-toggle-label">
              <input
                type="checkbox"
                checked={searchInBody}
                onChange={(e) => setSearchInBody(e.target.checked)}
                className="accent-[#9ec043] h-3 w-3"
                data-testid="inq-search-body-toggle"
              />
              Auch Nachrichteninhalt durchsuchen
            </label>
          </div>
          <div className="grid grid-cols-3 gap-1 mt-2" data-testid="inq-status-tabs">
            {[
              { id: 'open',        label: 'Offen',          n: counts.open || 0,        active: 'bg-red-50 border-red-300 text-red-700' },
              { id: 'in_progress', label: 'In Bearbeitung', n: counts.in_progress || 0, active: 'bg-amber-50 border-amber-300 text-amber-700' },
              { id: 'closed',      label: 'Erledigt',       n: counts.closed || 0,      active: 'bg-emerald-50 border-emerald-300 text-emerald-700' },
            ].map(s => (
              <button key={s.id} onClick={() => setStatusFilter(s.id)}
                      className={`px-1.5 py-1 text-[10px] font-semibold rounded border transition-colors ${
                        statusFilter === s.id
                          ? s.active
                          : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                      data-testid={`inq-status-${s.id}`}>
                {s.label} <span className="opacity-70">({s.n})</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {canDelete && items.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50 text-[11px]"
                 data-testid="inq-bulk-bar">
              <Checkbox checked={items.length > 0 && bulkIds.size === items.length}
                        onCheckedChange={v => selectAllBulk(!!v)}
                        data-testid="inq-select-all" />
              <span className="text-gray-500">Alle · {bulkIds.size} markiert</span>
              <Button onClick={bulkDelete} disabled={bulkIds.size === 0 || bulkDeleting}
                      variant="outline"
                      className="ml-auto h-6 text-[10px] text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40 px-2"
                      data-testid="btn-inq-bulk-delete">
                <Trash2 className="w-3 h-3 mr-1" /> Löschen
              </Button>
            </div>
          )}
          {loading && <div className="p-6 text-center text-xs text-gray-400">Lade…</div>}
          {!loading && filteredItems.length === 0 && (
            <div className="p-6 text-center text-xs text-gray-400" data-testid="inq-empty">
              {searchQuery ? `Keine Treffer für „${searchQuery}".` : 'Keine Einträge.'}
            </div>
          )}
          {filteredItems.map(inq => (
            <div key={inq.id}
                 className={`w-full flex items-start gap-2 p-3 border-b border-gray-100 ${selected?.id === inq.id ? 'bg-[#9ec043]/5' : 'hover:bg-gray-50'}`}
                 data-testid={`inq-row-${inq.id}`}>
              {canDelete && (
                <Checkbox checked={bulkIds.has(inq.id)}
                          onCheckedChange={() => toggleBulk(inq.id)}
                          className="mt-1 shrink-0"
                          data-testid={`inq-row-select-${inq.id}`} />
              )}
              <button onClick={() => setSelected(inq)}
                      className="flex-1 text-left flex items-start gap-2 min-w-0">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${inq.status === 'new' ? 'bg-red-500' : inq.status === 'done' ? 'bg-green-500' : 'bg-amber-500'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-[#32373c] truncate flex items-center gap-1">
                    {inq.aiSummaryDoc?.discrepancyWarning && (
                      <span className="text-rose-500 shrink-0" title="KI hat eine Unstimmigkeit erkannt — bitte prüfen"
                            data-testid={`flag-discrepancy-${inq.id}`}>
                        <Flag className="w-3 h-3 inline -mt-0.5" fill="currentColor" />
                      </span>
                    )}
                    <span className="truncate">{inq.subject}</span>
                  </div>
                  <div className="text-[10px] text-gray-400 truncate">{inq.customerName || '—'} · {inq.customerEmail}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">{fmtDate(inq.receivedAt || inq.createdAt)}</div>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    {inq.urgency === 'high' && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 inline-block">Dringend</span>}
                    {inq.aiSummaryDoc?.discrepancyWarning && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 inline-block"
                            title={inq.aiSummaryDoc.discrepancyWarning}>
                        Unstimmigkeit
                      </span>
                    )}
                    {Array.isArray(inq.mailThread) && inq.mailThread.length > 0 && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 inline-block">
                        +{inq.mailThread.length} Mail{inq.mailThread.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Right: detail — mobile: nur sichtbar wenn ausgewählt (+ Zurück-Button) */}
      <div className={`${selected ? 'flex' : 'hidden lg:flex'} flex-1 bg-[#f5f6f7] overflow-y-auto min-w-0 flex-col`}>
        {selected ? (
          <div className="p-4 sm:p-6 max-w-4xl w-full" data-testid="inq-detail">
            {/* Mobile-Zurück-Button */}
            <button
              onClick={() => setSelected(null)}
              className="lg:hidden inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#9ec043] mb-3"
              data-testid="inq-back-to-list"
            >
              ← Zurück zur Liste
            </button>
            <div className="mb-4">
              <h2 className="text-xl font-bold text-[#32373c] flex items-center gap-2 flex-wrap">
                {selected.subject}
                {selected.externalOrderId && (
                  <span className="px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide bg-[#9ec043]/10 text-[#7a9234] border border-[#9ec043]/30"
                        data-testid="external-order-id"
                        title="Externe Bestell-ID — Ergänzungs-Mails werden automatisch hier gebündelt.">
                    {selected.externalOrderId}
                  </span>
                )}
                {Array.isArray(selected.mailThread) && selected.mailThread.length > 0 && (
                  <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-700"
                        title="Anzahl gebündelter Folge-/Ergänzungs-Mails">
                    +{selected.mailThread.length} Mail{selected.mailThread.length !== 1 ? 's' : ''}
                  </span>
                )}
              </h2>
              <div className="text-xs text-gray-400 mt-1">
                Von {selected.customerName || '—'} &lt;{selected.customerEmail}&gt; · Eingegangen {fmtDate(selected.receivedAt || selected.createdAt)}
              </div>
              <InquiryOrderLink inquiry={selected} onUpdated={(fresh) => { setSelected(fresh); load(); }} />

              {/* Status- + Move-Bar — Workflow „Offen/In Bearbeitung/Erledigt"
                  gilt jetzt für ALLE Anfrage-Kategorien, nicht nur Bestellanfragen. */}
              <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="inq-action-bar">
                <div className="flex items-center gap-1 border border-gray-200 rounded-lg overflow-hidden">
                    {[
                      { id: 'new',         label: 'Offen',          activeCls: 'bg-red-50 text-red-700' },
                      { id: 'in_progress', label: 'In Bearbeitung', activeCls: 'bg-amber-50 text-amber-700' },
                      { id: 'closed',      label: 'Erledigt',       activeCls: 'bg-emerald-50 text-emerald-700' },
                    ].map(s => {
                      const cur = selected.status || 'new';
                      const active = cur === s.id || (s.id === 'closed' && cur === 'done');
                      return (
                        <button key={s.id}
                                onClick={async () => {
                                  const r = await authFetch(`/api/mail-inbound/inquiries/${selected.id}`, {
                                    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ status: s.id }),
                                  });
                                  if (r.ok) { const fresh = await r.json(); setSelected(fresh); load(); }
                                }}
                                className={`px-3 py-1 text-[11px] font-semibold transition-colors ${
                                  active ? s.activeCls : 'text-gray-500 hover:bg-gray-50'
                                }`}
                                data-testid={`inq-set-status-${s.id}`}>
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                <div className="ml-auto flex items-center gap-1 text-[11px]">
                  <Move className="w-3 h-3 text-gray-400" />
                  <span className="text-gray-400">Verschieben in:</span>
                  <select value={selected.kind || 'general'}
                          onChange={async (e) => {
                            const newKind = e.target.value;
                            if (newKind === selected.kind) return;
                            const r = await authFetch(`/api/mail-inbound/inquiries/${selected.id}`, {
                              method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ kind: newKind }),
                            });
                            if (r.ok) {
                              setSelected(null);
                              load();
                            } else {
                              alert('Verschieben fehlgeschlagen');
                            }
                          }}
                          className="text-[11px] px-1.5 py-0.5 rounded border border-gray-200 bg-white"
                          data-testid="inq-move-select">
                    {TABS.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Schnell-Aktionen — Task & Mail-Reply, oben statt unten */}
              <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="inq-quick-actions">
                <CreateTaskFromInquiryButton inquiry={selected} />
                <Button variant="outline" size="sm" onClick={() => {
                  const subject = encodeURIComponent(`Re: ${selected.subject}`);
                  const body = encodeURIComponent(`Guten Tag ${selected.customerName || ''},\n\nvielen Dank für Ihre Anfrage.\n\n`);
                  window.location.href = `mailto:${selected.customerEmail}?subject=${subject}&body=${body}`;
                }} className="h-7 text-[11px]" data-testid="btn-inq-reply">
                  <Mail className="w-3 h-3 mr-1" /> Antworten (Mail-Client)
                </Button>
                {canDelete && (
                  <Button variant="outline" size="sm" onClick={() => remove(selected)}
                          className="h-7 text-[11px] text-red-600 border-red-200 hover:bg-red-50 ml-auto"
                          data-testid="btn-inq-delete">
                    <Trash2 className="w-3 h-3 mr-1" /> Löschen
                  </Button>
                )}
              </div>
            </div>

            {/* KI-Summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
              <h3 className="text-sm font-semibold text-[#32373c] flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#9ec043]" /> KI-Zusammenfassung & Produktempfehlung
                <button type="button"
                        onClick={async () => {
                          if (reanalyzing) return;
                          setReanalyzing(true);
                          try {
                            const res = await authFetch(`/api/mail-inbound/inquiries/${selected.id}/reanalyze`, { method: 'POST' });
                            if (res.ok) {
                              const fresh = await res.json();
                              setSelected(fresh);
                              load();
                            } else {
                              const d = await res.json().catch(() => ({}));
                              alert(d.detail || 'Neu-Analyse fehlgeschlagen');
                            }
                          } finally { setReanalyzing(false); }
                        }}
                        disabled={reanalyzing}
                        className="ml-auto text-[11px] text-gray-400 hover:text-[#7ca735] flex items-center gap-1 disabled:opacity-60 disabled:cursor-progress"
                        title="Klassifizierung mit aktuellem Produktkatalog neu ausführen"
                        data-testid="btn-inq-reanalyze">
                  <RefreshCw className={`w-3 h-3 ${reanalyzing ? 'animate-spin' : ''}`} />
                  {reanalyzing ? 'Analysiert…' : 'Neu analysieren'}
                </button>
              </h3>
              {selected.aiSummary && (
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mb-3">{selected.aiSummary}</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                {selected.productRecommendation && (
                  <InfoBox label={`Produktempfehlung${selected.productRecommendationSource === 'attachment' ? ' (aus Anhang)' : ''}`}
                           value={selected.productRecommendation} color="green" />
                )}
                {selected.customerIntent && (
                  <InfoBox label="Kundenwunsch" value={selected.customerIntent} />
                )}
                {selected.nextStepForTeam && (
                  <InfoBox label="Nächster Schritt" value={selected.nextStepForTeam} />
                )}
              </div>

              {selected.productExplanation?.bullets?.length > 0 && (
                <div className="mt-3 border border-[#9ec043]/30 bg-[#9ec043]/5 rounded-lg px-3 py-2.5"
                     data-testid="inq-product-explanation">
                  <div className="text-[11px] text-[#7ca735] uppercase tracking-wide font-semibold mb-1.5">
                    Empfehlung im Detail
                  </div>
                  <ul className="space-y-1">
                    {selected.productExplanation.bullets.map((b, i) => (
                      <li key={i} className="text-xs text-gray-700 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: b.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                    ))}
                  </ul>
                </div>
              )}

              {/* Trigger-Trace: zeigt durchsichtig welche Regex/Domain-Regel
                  zu welcher Auslegungs-Entscheidung geführt hat — kollabiert
                  per Default, damit die Sidebar ruhig bleibt. */}
              {Array.isArray(selected.sizingTrace) && selected.sizingTrace.length > 0 && (
                <SizingTraceBlock trace={selected.sizingTrace} />
              )}

              {selected.productRecommendation && (
                <WhatIfPanel
                  key={`whatif-${selected.id}-${selected.customerData?.updatedAt || selected.updatedAt || ''}`}
                  endpoint="/api/mail-inbound/what-if"
                  authHeader={{ Authorization: `Bearer ${localStorage.getItem('ees_token') || ''}` }}
                  initialPvKwp={(() => {
                    const cd = selected.customerData || {};
                    if (cd.pvKwp) return Number(cd.pvKwp);
                    // Aus extrahierten PV-Designdaten: Σ (Modulanzahl × Wp) / 1000
                    const fields = selected.pvDesign?.fields || [];
                    if (fields.length) {
                      const totalKwp = fields.reduce((acc, f) => {
                        const cnt = Number(f.moduleCount) || 0;
                        const wp = Number(f.powerWpPerModule) || 0;
                        return acc + (cnt * wp) / 1000;
                      }, 0);
                      if (totalKwp > 0) return Math.round(totalKwp * 100) / 100;
                    }
                    // Body- UND Produktempfehlungs-Text durchsuchen (z. B.
                    // "14,72 kWp" mit 2 Nachkommastellen — alte Regex matchte
                    // nur 1 Stelle und fiel auf 7 kWp Default zurück).
                    const haystack = (selected.bodyText || '') + ' ' +
                                     (selected.productRecommendation || '');
                    const m = /(\d{1,3}(?:[.,]\d{1,3})?)\s*k[wW]p/.exec(haystack);
                    return m ? parseFloat(m[1].replace(',', '.')) : 7;
                  })()}
                  initialHasExistingPv={(() => {
                    const cd = selected.customerData || {};
                    if (typeof cd.hasExistingPv === 'boolean') return cd.hasExistingPv;
                    return !!selected.externalPvInverter;
                  })()}
                  initialBuildingType={(() => {
                    const cd = selected.customerData || {};
                    if (cd.buildingType === 'mfh' || cd.buildingType === 'mehrfamilienhaus') return 'multi_family';
                    if (cd.buildingType === 'villa' || cd.buildingType === 'altbau') return 'villa';
                    if (cd.buildingType === 'efh') return 'standard';
                    const t = (selected.bodyText || '').toLowerCase();
                    if (/mehrfamilien|doppelhaus/.test(t)) return 'multi_family';
                    if (/villa|altbau|landhaus|anwesen/.test(t)) return 'villa';
                    return 'standard';
                  })()}
                  initialProfileClass={(() => {
                    const t = ((selected.bodyText || '') + ' ' + (selected.subject || '')).toLowerCase();
                    // PRIVATE Klassen ZUERST prüfen — sonst matcht "handel"
                    // in Signatur/Footer fälschlich auf retail, obwohl der
                    // Anfragetext klar Villa/EFH/MFH ist.
                    if (/\bvilla\b|\baltbau\b|\blandhaus\b|\banwesen\b/.test(t)) return 'private_villa';
                    if (/\bmehrfamilien|\bmieterstrom\b|\bwohnanlage\b/.test(t)) return 'private_mfh';
                    if (/\beinfamilien|\bprivathaus\b|\bwohnhaus\b/.test(t)) return 'private_efh';
                    // Dann Gewerbe-Klassen mit Wortgrenzen
                    if (/\brestaurant\b|\bgastronom|\bcafé\b|\bcafe\b|\bhotel\b|\bpension\b/.test(t)) return 'gastro';
                    if (/\bbüro\b|\bpraxis\b|\bverwaltung\b|\bkanzlei\b|\bagentur\b/.test(t)) return 'office';
                    if (/\bwerkstatt\b|\bhandwerk\b|\bkfz\b|\bmetallbau\b|\bschlosser/.test(t)) return 'workshop';
                    if (/\bproduktion\b|\bfertigung\b|\bindustrie\b|\bcnc\b/.test(t)) return 'production';
                    if (/\beinzelhandel\b|\bsupermarkt\b|\bverkaufsfläche\b|\bfriseur\b/.test(t)) return 'retail';
                    if (/\btierhaltung\b|\bmelk|\bstall\b/.test(t)) return 'agri_livestock';
                    if (/\blandwirtschaft\b|\bbauernhof\b/.test(t)) return 'agri_general';
                    return 'private_efh';
                  })()}
                  initialBackupKw={(() => {
                    const cd = selected.customerData || {};
                    if (cd.backupKw) return Number(cd.backupKw);
                    if (selected.backupKw) return Number(selected.backupKw);
                    // _deterministicMatch.backup.load_kw aus classifierMeta
                    const meta = selected.classifierMeta || {};
                    const detBackup = meta._deterministicMatch?.backup;
                    if (detBackup?.load_kw) return Number(detBackup.load_kw);
                    // Fallback: Notstrom-Patterns im Body (40 kW im Notstromfall etc.)
                    const txt = (selected.bodyText || '') + ' ' + (selected.subject || '');
                    const m = /(?:notstrom|notbetrieb|notstromfall|hausverbrauch[^.;\n]{0,40}?)(\d{1,3}(?:[.,]\d{1,2})?)\s*kw\b(?!p)/i.exec(txt);
                    return m ? parseFloat(m[1].replace(',', '.')) : 0;
                  })()}
                  initialPinnedModel={(() => {
                    const rec = selected.productRecommendation || '';
                    // Extrahiere "HS 9 PV duo" / "HS 15" / "GS 60" / "GS HV 60" etc.
                    const m = /\b(HS\s+PV\s+duo\s+\d+|HS\s+\d+\s+PV\s+duo|HS\s+\d+\s+PV|HS\s+\d+|GS\s+HV\s+\d+|GS\s+\d+)\b/i.exec(rec);
                    if (!m) return null;
                    // Normalisierung: erste Zahl + Familie umsortieren wenn nötig
                    let model = m[1].replace(/\s+/g, ' ').trim();
                    // "HS PV duo 9" → "HS 9 PV duo"
                    const swap = /^HS\s+(PV(?:\s+duo)?)\s+(\d+)$/i.exec(model);
                    if (swap) model = `HS ${swap[2]} ${swap[1]}`;
                    return model;
                  })()}
                  initialWantedKwh={(() => {
                    if (selected.wantedKwh) return selected.wantedKwh;
                    // Fallback für Alt-Anfragen ohne wantedKwh-Feld:
                    // Extraktion aus Produktempfehlung ("angefragten 12 kWh")
                    // oder Body ("benötigt ca. 14 kWh").
                    const haystack = (selected.productRecommendation || '') +
                                     ' ' + (selected.bodyText || '');
                    const m = /(?:angefragt(?:en?)?|wunsch|wünscht|benötigt|möcht\w+)[^\d]{0,30}(\d{1,3}(?:[.,]\d{1,3})?)\s*k[wW]h/i.exec(haystack);
                    return m ? parseFloat(m[1].replace(',', '.')) : null;
                  })()}
                />
              )}

              {(selected.productAlternatives?.length > 0 || selected.externalPvInverter) && (
                <div className="mt-3 border-t border-gray-100 pt-3" data-testid="inq-alternatives">
                  {selected.externalPvInverter && (
                    <div className="text-[11px] text-[#7ca735] bg-[#9ec043]/5 border border-[#9ec043]/30 rounded px-2 py-1 mb-2 inline-block">
                      AC-Kopplung erkannt: externer <strong>{selected.externalPvInverter === 'extern' ? 'PV-Wechselrichter' : selected.externalPvInverter.charAt(0).toUpperCase() + selected.externalPvInverter.slice(1)}</strong> vorhanden
                    </div>
                  )}
                  {selected.productAlternatives?.length > 0 && (
                    <>
                      <div className="text-[11px] text-gray-500 uppercase tracking-wide font-medium mb-1.5">Beratende Alternativen</div>
                      <ul className="space-y-1.5">
                        {selected.productAlternatives.map((alt, i) => {
                          const isCommercial = alt.kind === 'commercial';
                          return (
                            <li key={i}
                                className={`text-xs rounded px-2 py-1.5 ${
                                  isCommercial
                                    ? 'bg-amber-50 border border-amber-300 text-amber-900'
                                    : 'bg-gray-50 border border-gray-200 text-gray-700'
                                }`}
                                data-testid={`inq-alt-${i}${isCommercial ? '-commercial' : ''}`}>
                              {isCommercial && <span className="mr-1">⚡</span>}
                              <span className="font-semibold text-[#32373c]">{alt.model}</span>
                              <span className={isCommercial ? 'text-amber-800' : 'text-gray-500'}> — {alt.reason}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Attachment analysis (files are NOT stored — only extracted insight) */}
            <AttachmentAnalysisPanel inquiry={selected} />

            {/* Telefon-Notizen, die via Telefonassistent an diese Anfrage angehängt wurden */}
            <PhoneCallLogPanel inquiry={selected} />

            {/* Easybill match */}
            {selected.easybillCustomer && (
              <div className="bg-white rounded-xl border border-[#9ec043]/40 p-4 mb-4" data-testid="inq-customer-found">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <CheckCircle2 className="w-4 h-4 text-[#9ec043]" />
                  <h3 className="text-sm font-semibold text-[#32373c]">Kunde in Easybill gefunden</h3>
                  <Button size="sm" variant="outline"
                          onClick={() => rematchCustomer(selected.id)}
                          disabled={rematching === selected.id}
                          className="h-6 text-[10px] ml-auto" data-testid="btn-rematch-customer">
                    <RefreshCw className={`w-3 h-3 mr-1 ${rematching === selected.id ? 'animate-spin' : ''}`} />
                    {rematching === selected.id ? 'Suche…' : 'Neu suchen'}
                  </Button>
                </div>
                <div className="text-xs text-gray-700 flex items-center gap-2 flex-wrap">
                  <strong>{selected.easybillCustomer.company || `${selected.easybillCustomer.firstName || ''} ${selected.easybillCustomer.lastName || ''}`.trim()}</strong>
                  <span className="text-gray-400">#{selected.easybillCustomer.number}</span>
                  {selected.easybillCustomer.salePriceLevel && selected.easybillCustomer.salePriceLevel !== 'SALEPRICE' && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-blue-100 text-blue-700"
                          data-testid="customer-price-group"
                          title="Sonderpreisgruppe — Artikel-Preise im Angebot werden aus dieser Spalte gezogen.">
                      Preisgruppe {selected.easybillCustomer.salePriceLevel.replace('SALEPRICE', '') || '1'}
                    </span>
                  )}
                </div>
                {rematchMsg && <p className="text-[10px] text-gray-400 mt-2">{rematchMsg}</p>}
                {/* Falscher Kunde? Bei weitergeleiteten Mails kommt der Forwarder
                    statt des echten Kunden raus — daher MUSS das Anlage-Formular
                    auch hier verfügbar sein, um den korrekten Kunden zu ersetzen. */}
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-[10px] text-amber-600 mb-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Falscher Kunde? Bei weitergeleiteten Mails wird ggf. der Forwarder erkannt — du kannst hier den richtigen Kunden anlegen oder unten manuell zuordnen.
                  </p>
                  <CreateEasybillCustomerForm inquiry={selected}
                    buttonLabel="Anderen Kunden in Easybill anlegen"
                    onCreated={async () => {
                      const r = await authFetch(`/api/mail-inbound/inquiries/${selected.id}`);
                      if (r.ok) setSelected(await r.json());
                      load();
                    }} />
                </div>
              </div>
            )}
            {!selected.easybillCustomer && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4" data-testid="inq-no-customer">
                <div className="flex items-center gap-2 flex-wrap">
                  <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                  <p className="text-xs text-gray-500 flex-1">Kunde nicht in Easybill gefunden — bitte erneut suchen oder als Neukunde anlegen.</p>
                  <Button size="sm" variant="outline"
                          onClick={() => rematchCustomer(selected.id)}
                          disabled={rematching === selected.id}
                          className="h-7 text-[11px]" data-testid="btn-rematch-customer">
                    <RefreshCw className={`w-3 h-3 mr-1 ${rematching === selected.id ? 'animate-spin' : ''}`} />
                    {rematching === selected.id ? 'Suche…' : 'Erneut bei Easybill suchen'}
                  </Button>
                </div>
                {rematchMsg && <p className="text-[10px] text-gray-400 mt-2">{rematchMsg}</p>}
                <CreateEasybillCustomerForm inquiry={selected} onCreated={async () => {
                  const r = await authFetch(`/api/mail-inbound/inquiries/${selected.id}`);
                  if (r.ok) setSelected(await r.json());
                  load();
                }} />
              </div>
            )}

            {/* Manuelle Kunden-Zuordnung — immer verfügbar, nicht nur bei missing */}
            <ManualCustomerAssign inquiry={selected} onAssigned={() => {
              load();
              (async () => {
                const r = await authFetch(`/api/mail-inbound/inquiries/${selected.id}`);
                if (r.ok) setSelected(await r.json());
              })();
              // Nach manueller Kunden-Zuordnung MUSS das Angebot-Prefill neu
              // gezogen werden — Easybill liefert je nach `salePriceLevel`
              // des Kunden andere Preise (Preisgruppe 2..10). Ohne Bump
              // bleiben die zuvor geladenen Standardpreise stehen und der
              // Innendienst sieht falsche kundenspezifische Preise.
              setPrefillRefreshTick(t => t + 1);
            }} />

            {/* G1: Offer draft.
                04.02.2026 (kritischer Bugfix!): `key={selected.id}` zwingt
                React die Komponente bei jedem Anfragen-Wechsel neu zu
                mounten. Vorher blieb der lokale `title`-State (initialisiert
                nur beim ersten Mount aus `inquiry.subject`) beim Wechsel
                erhalten — das führte dazu, dass die Kommission einer
                zuvor geöffneten Anfrage in Easybill-Aufträge einer anderen
                Anfrage übernommen wurde (siehe Live-Vorfall bysolar
                „BV Görlitz" → Auftragsentwurf mit Titel „BV Schaal"). */}
            <OfferDraftPanel key={selected.id} inquiry={selected} refreshTick={prefillRefreshTick}
                             onCreated={() => { load(); }} onAfterOrder={() => { load(); }} />

            {/* Strukturierte Anfrage-Daten + KI-Zusammenfassung */}
            <CustomerDataForm inquiry={selected} onChange={async () => {
              const r = await authFetch(`/api/mail-inbound/inquiries/${selected.id}`);
              if (r.ok) setSelected(await r.json());
              setPrefillRefreshTick(t => t + 1);
            }} />
            <AiSummaryPanel inquiry={selected} onChange={async () => {
              const r = await authFetch(`/api/mail-inbound/inquiries/${selected.id}`);
              if (r.ok) setSelected(await r.json());
            }} />

            {/* String-Auslegung mit PDF (für PV-Konfigurator-Workflow) */}
            <StringDesignPanel inquiry={selected} onChange={async () => {
              const r = await authFetch(`/api/mail-inbound/inquiries/${selected.id}`);
              if (r.ok) setSelected(await r.json());
            }} />

            {/* Notizen + Mail-Thread */}
            <NotesAndThreadPanel inquiry={selected} onChange={() => {
              (async () => {
                const r = await authFetch(`/api/mail-inbound/inquiries/${selected.id}`);
                if (r.ok) setSelected(await r.json());
              })();
            }} />

            {/* Original */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
              <h3 className="text-sm font-semibold text-[#32373c] mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" /> Original-Mail
              </h3>
              {selected.bodyHtml ? (
                <div className="text-xs text-gray-700 prose-sm" dangerouslySetInnerHTML={{ __html: selected.bodyHtml }} />
              ) : (
                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans">{selected.bodyText}</pre>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm">
            Eintrag auswählen
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

function InfoBox({ label, value, color }) {
  const bg = color === 'green' ? 'bg-[#9ec043]/5 border-[#9ec043]/30' : 'bg-gray-50 border-gray-200';
  return (
    <div className={`rounded-lg border p-2 ${bg}`}>
      <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">{label}</div>
      <div className="text-xs text-[#32373c]">{value}</div>
    </div>
  );
}

// Trigger-Trace: macht durchsichtig, welche Domain-Regeln HANS bei der
// Modellempfehlung gefeuert hat. Reine Lesehilfe für die Innendienst-Truppe,
// damit Kalibrier-Fälle ohne Backend-Logs gespotted werden können.
const SIZING_TRACE_DOCS = {
  pv_kwp_detection: {
    title: 'PV-Größe aus Mail-Text',
    body: 'Regex `_KWP_RE` sucht die größte plausible kWp-Angabe (bis 70 kWp). Erkennt Schreibweisen wie „8,5 kWp", „10 kW peak" und „PV-Anlage mit 12kWp". 800 Wp / 2 kW (ohne „p") werden NICHT als PV-Größe erkannt — das wäre dann Bestands-AC.',
  },
  ext_pv_inverter: {
    title: 'Bestands-PV (AC-gekoppelt)',
    body: 'Wenn der Kunde eine bestehende PV-Anlage (z. B. SMA / Fronius / Kostal) oder ein Balkonkraftwerk hat, ist die PV bereits AC-gekoppelt. Der Speicher braucht dann KEIN HS PV (Hybrid-DC), sondern die reine HS-Familie (AC-Ladung über den Hausanschluss). Auch Steckersolar und Mikrowechselrichter zählen hier rein.',
  },
  backup_detection: {
    title: 'Notstrom-Erkennung',
    body: 'Sobald irgendwo „Notstrom", „Backup", „Ersatzstrom" oder „Inselbetrieb" steht, schaltet HANS auf backup-aware Sizing um. Min-Entlade- und Min-Lade-Leistung werden aus Hausverbrauch + PV abgeleitet, und das Modell muss diese Werte zwingend erfüllen — nicht nur die kWh.',
  },
  backup_urgency_soft: {
    title: 'Konjunktiv: Notstrom soft',
    body: 'Formulierungen wie „später möglich", „wünschenswert", „nachrüstbar", „nice-to-have", „wäre schön", „in Zukunft denkbar" gelten als weiche Anforderung. Min-Modell wird auf HS 9 entschärft (statt HS 15 bei BHKW). Auslegung als Reserve, ohne Vollbestückung für ungewissen Zeitpunkt.',
  },
  backup_urgency_hard: {
    title: 'Notstrom hart',
    body: 'Formulierungen wie „zwingend notwendig", „24/7-Betrieb", „muss möglich sein", „darf nicht ausfallen", „kritische Infrastruktur" gelten als harte Anforderung. Min-Modell strikt — bei BHKW → HS 15, bei Generator → HS 9.',
  },
  chp_min_model: {
    title: 'BHKW-Trigger',
    body: 'BHKW im Notstrom-Kontext erzwingt mindestens HS 15 (bei harter Anforderung), weil die kombinierte Last aus Wärmepumpe, Eigenverbrauch und ggf. Mieterstrom im Inselbetrieb getragen werden muss. Bei weicher Formulierung wird auf HS 9 entschärft. Erkannt werden: BHKW, KWK-Anlage, Blockheizkraftwerk, Mini-BHKW, Kraft-Wärme-Kopplung.',
  },
  generator_min_model: {
    title: 'Generator-Trigger',
    body: 'Sobald ein konkreter Notstromgenerator (z. B. „Diesel-Aggregat 8 kW", „Zapfwellengenerator 12 kVA") erkannt wird, ist HS 3/5/6 nicht mehr sinnvoll — Genlink-fähig sind nur HS 9 und HS 15. Min-Modell = HS 9.',
  },
  load_kw_detection: {
    title: 'Lastspitze',
    body: 'Hausverbrauch / Last in kW (z. B. „Spitzenlast 8 kW", „Hausanschluss 20 kW"). Definiert die Min-Entladeleistung des Speichers im Inselbetrieb. Wenn das Modell weniger Entladung kann, eskaliert HANS auf das nächstgrößere.',
  },
  family_forcing_gs: {
    title: 'GS-Familie erzwungen',
    body: 'GS / GS HV ist Pflicht, wenn entweder die Last > 13 kW oder die PV > 24 kW liegt (bei AC-Bestands-PV schon ab 13 kW PV). Bei „Outdoor"-Installation wird GS HV (Hochvolt) bevorzugt. Auch GS-Schlüsselwörter wie „Gewerbespeicher", „Industriespeicher", „Mieterstrom" eskalieren auf GS.',
  },
  wanted_kwh: {
    title: 'Wunsch-kWh',
    body: 'Aus dem Mail-Text extrahiert (z. B. „15 kWh", „10 kWh nutzbar"). Wenn keine Angabe → Default-Heuristik aus PV-Größe und Jahresverbrauch. Steuert das primäre kWh-Matching.',
  },
  min_model_pin: {
    title: 'Mindestmodell-Pin',
    body: 'Verhindert, dass kleinere Modelle (HS 3 / HS 5 / HS 6) auch bei passender kWh gewählt werden, wenn ein Domain-Trigger (BHKW oder Generator) ein größeres Modell erfordert. Greift NUR bei is_backup=True.',
  },
  best_match_selection: {
    title: 'Finale Auswahl',
    body: 'Aus den Kandidaten (alle Modelle mit kWh ≥ Wunsch − 10%, Familie passt, Entlade-/Lade-Leistung erfüllt, Min-Modell erfüllt) wird das KLEINSTE gewählt. Damit landet der Kunde nicht versehentlich auf HS 15, wenn HS 9 reicht.',
  },
};

function SizingTraceBlock({ trace }) {
  const [open, setOpen] = useState(false);
  const [docFor, setDocFor] = useState(null);
  return (
    <div className="mt-3 border border-gray-200 rounded-lg bg-white" data-testid="inq-sizing-trace">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50 rounded-lg"
        data-testid="inq-sizing-trace-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
            Sizing-Trace
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-mono">
            {trace.length}
          </span>
        </div>
        <span className="text-[10px] text-gray-400">{open ? 'einklappen' : 'warum dieses Modell?'}</span>
      </button>
      {open && (
        <ol className="px-3 pb-3 pt-1 space-y-2 border-t border-gray-100">
          {trace.map((t, i) => {
            const doc = t.docKey ? SIZING_TRACE_DOCS[t.docKey] : null;
            const showDoc = docFor === i;
            return (
              <li key={i} className="text-[11px]" data-testid={`inq-trace-${i}`}>
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-[9px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-500 shrink-0">
                    {t.rule}
                  </span>
                  <span className="text-gray-800 flex-1">{t.outcome}</span>
                  {doc && (
                    <button
                      type="button"
                      onClick={() => setDocFor(showDoc ? null : i)}
                      className="shrink-0 text-[9px] text-[#9ec043] hover:text-[#7ca735] underline-offset-2 hover:underline"
                      data-testid={`inq-trace-${i}-more`}
                    >
                      {showDoc ? 'weniger' : 'mehr erfahren'}
                    </button>
                  )}
                </div>
                {t.evidence && (
                  <div className="mt-0.5 pl-2 border-l-2 border-gray-200 ml-1 text-[10px] text-gray-500 italic leading-snug">
                    {t.evidence}
                  </div>
                )}
                {showDoc && doc && (
                  <div className="mt-1.5 ml-1 px-2.5 py-2 bg-[#9ec043]/5 border-l-2 border-[#9ec043] rounded-r text-[10px] leading-relaxed"
                       data-testid={`inq-trace-${i}-doc`}>
                    <div className="font-semibold text-[#32373c] mb-1">{doc.title}</div>
                    <div className="text-gray-700">{doc.body}</div>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function PhoneCallLogPanel({ inquiry }) {
  const log = inquiry.phoneCallLog || [];
  if (!log.length) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4" data-testid="phone-call-log-panel">
      <h3 className="text-sm font-semibold text-[#32373c] mb-2 flex items-center gap-2">
        <PhoneCall className="w-4 h-4 text-[#9ec043]" />
        Telefon-Notizen ({log.length})
      </h3>
      <div className="space-y-2">
        {log.map(e => (
          <div key={e.id} className="bg-gray-50 border border-gray-100 rounded-lg p-2.5 text-xs"
               data-testid={`phone-log-entry-${e.id}`}>
            <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-1">
              <span>{new Date(e.createdAt).toLocaleString('de-DE')}</span>
              <span>·</span>
              <span>{e.createdByName || e.createdBy}</span>
              {e.caseType && (
                <span className="ml-auto px-1.5 py-0.5 rounded-full bg-white border border-gray-200">
                  {e.caseType}
                </span>
              )}
            </div>
            <div className="font-medium text-[#32373c]">{e.title}</div>
            {e.shortSummary && (
              <div className="text-gray-600 mt-1 leading-relaxed">{e.shortSummary}</div>
            )}
            {e.productRecommendation && (
              <div className="mt-1.5 text-[11px] bg-[#9ec043]/10 border border-[#9ec043]/30 rounded px-2 py-1">
                <span className="text-gray-500">Empfehlung: </span>
                <span className="text-[#32373c]">{e.productRecommendation}</span>
              </div>
            )}
            {(e.openQuestions || []).length > 0 && (
              <div className="mt-1.5">
                <div className="text-[10px] uppercase tracking-wide text-gray-400">Offene Fragen</div>
                <ul className="text-[11px] text-gray-600 mt-0.5">
                  {e.openQuestions.map((q, i) => <li key={i}>· {q}</li>)}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function fmtBytes(b) {
  if (!b || b < 1024) return `${b || 0} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function CreateTaskFromInquiryButton({ inquiry }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [assignee, setAssignee] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  const openDialog = async () => {
    setTitle(`Anfrage: ${inquiry.subject}`.slice(0, 120));
    setAssignee('');
    setPriority(inquiry.urgency === 'high' ? 'high' : 'medium');
    setDueDate('');
    setMsg(null);
    setOpen(true);
    // Load users list (admin-only endpoint — silently falls back to empty for other roles)
    const res = await authFetch('/api/auth/users');
    if (res.ok) setUsers(await res.json());
  };

  const submit = async () => {
    if (!title.trim()) { setMsg('Titel eingeben'); return; }
    setSubmitting(true); setMsg(null);
    const res = await authFetch('/api/tasks', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: title.trim(),
        description: inquiry.aiSummary || inquiry.bodyText?.slice(0, 500) || '',
        priority,
        status: 'todo',
        dueDate: dueDate || null,
        source: 'inquiry',
        sourceId: inquiry.id,
        sourceLabel: `Anfrage · ${inquiry.customerName || inquiry.customerEmail || ''}`,
        assignees: assignee ? [assignee] : [],
      }),
    });
    setSubmitting(false);
    if (res.ok) {
      setMsg('Aufgabe angelegt — sichtbar im Tab „Aufgaben".');
      setTimeout(() => setOpen(false), 1200);
    } else {
      const d = await res.json().catch(() => ({}));
      setMsg(d.detail || 'Aufgabe konnte nicht erstellt werden');
    }
  };

  return (
    <>
      <Button variant="outline" onClick={openDialog} className="h-9 text-sm"
              data-testid="btn-inq-create-task">
        <Plus className="w-3.5 h-3.5 mr-1" /> Aufgabe erstellen
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
             onClick={() => !submitting && setOpen(false)}
             data-testid="inq-task-dialog-backdrop">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
               onClick={e => e.stopPropagation()}
               data-testid="inq-task-dialog">
            <h3 className="text-sm font-bold text-[#32373c] mb-4 flex items-center gap-2">
              <Plus className="w-4 h-4 text-[#9ec043]" /> Aufgabe aus Anfrage erstellen
            </h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500">Titel</Label>
                <Input value={title} onChange={e => setTitle(e.target.value)}
                       className="h-9 text-sm mt-1" data-testid="inq-task-title" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Mitarbeiter</Label>
                <select value={assignee} onChange={e => setAssignee(e.target.value)}
                        className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
                        data-testid="inq-task-assignee">
                  <option value="">Nicht zugewiesen</option>
                  {users.map(u => (
                    <option key={u.email} value={u.email}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-gray-500">Priorität</Label>
                  <select value={priority} onChange={e => setPriority(e.target.value)}
                          className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
                          data-testid="inq-task-priority">
                    <option value="low">Niedrig</option>
                    <option value="medium">Mittel</option>
                    <option value="high">Hoch</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Fällig am (optional)</Label>
                  <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                         className="h-9 text-sm mt-1" data-testid="inq-task-due" />
                </div>
              </div>
              {msg && <div className="text-xs text-gray-600">{msg}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}
                        className="h-9 text-xs" data-testid="inq-task-cancel">
                  Abbrechen
                </Button>
                <Button onClick={submit} disabled={submitting || !title.trim()}
                        className="h-9 text-xs bg-[#9ec043] hover:bg-[#8aad36] text-white"
                        data-testid="inq-task-submit">
                  {submitting ? 'Erstelle…' : 'Aufgabe anlegen'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AttachmentAnalysisPanel({ inquiry }) {
  const sums = inquiry.attachmentSummaries || [];
  const [expanded, setExpanded] = useState(null);  // attachmentId of currently-open preview
  if (!sums.length) return null;

  const backendUrl = API_BASE;
  const token = localStorage.getItem('ees_token') || '';
  const streamUrl = (attId) =>
    `${backendUrl}/api/mail-inbound/inquiries/${inquiry.id}/attachments/${attId}?token=${encodeURIComponent(token)}`;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4" data-testid="attachment-analysis-panel">
      <h3 className="text-sm font-semibold text-[#32373c] mb-2 flex items-center gap-2">
        <Paperclip className="w-4 h-4 text-[#9ec043]" />
        Anhänge ausgewertet
        <span className="text-[10px] text-gray-400 font-normal">(nur diese Anfrage · nicht in Wissensdatenbank)</span>
      </h3>
      {inquiry.attachmentInsight && (
        <p className="text-xs text-gray-700 mb-3 leading-relaxed">{inquiry.attachmentInsight}</p>
      )}
      <div className="space-y-1.5">
        {sums.map((a, i) => {
          const Icon = a.kind === 'image' ? ImageIcon : FileText;
          const attId = a.attachmentId;
          const isOpen = expanded === attId;
          const canPreview = !!attId && (a.mimeType === 'application/pdf' || (a.mimeType || '').startsWith('image/'));
          return (
            <div key={attId || i} className="text-xs bg-gray-50 rounded-lg border border-gray-100"
                 data-testid={`attachment-row-${i}`}>
              <div className="flex items-start gap-2 p-2">
                <Icon className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#32373c] truncate">{a.name}</span>
                    <span className="text-[10px] text-gray-400">{fmtBytes(a.sizeBytes)}</span>
                    {a.extractedProduct && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#9ec043]/10 text-[#7ca735] border border-[#9ec043]/30">
                        gefunden: {a.extractedProduct}
                      </span>
                    )}
                  </div>
                  {a.excerpt && (
                    <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{a.excerpt}</div>
                  )}
                </div>
                {canPreview && (
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : attId)}
                    className="text-[11px] text-[#7ca735] hover:text-[#5a8525] whitespace-nowrap px-2 py-1 rounded hover:bg-[#9ec043]/10"
                    data-testid={`btn-attachment-preview-${i}`}>
                    {isOpen ? 'Schließen' : 'Ansehen'}
                  </button>
                )}
              </div>
              {isOpen && canPreview && (
                <div className="border-t border-gray-200 p-2 bg-white" data-testid={`attachment-preview-${i}`}>
                  {a.mimeType === 'application/pdf' ? (
                    <iframe src={streamUrl(attId)} title={a.name}
                            className="w-full h-[560px] rounded border border-gray-200" />
                  ) : (
                    <img src={streamUrl(attId)} alt={a.name}
                         className="max-w-full max-h-[560px] mx-auto block rounded border border-gray-200" />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────
// G1: Angebotsentwurf aus Anfrage (Easybill Offer Draft)
// ─────────────────────────────────────────────────────
function emptyItem() {
  return { kind: 'standard', description: '', quantity: 1, single_price_net: 0, vat_percent: 19, unit: 'Stück', number: '' };
}

// ─────────────────────────────────────────────────────
// Neukunde direkt aus Anfrage in Easybill anlegen
//
// WICHTIG: KEINE Auto-Übernahme aus FROM-Header (das wäre bei
// weitergeleiteten Mails der Forwarder, nicht der Endkunde). Stattdessen
// lassen wir Gemini den Body lesen und Kundendaten aus Signatur/Original-
// Mail-Header extrahieren. Nutzer prüft + korrigiert + bestätigt.
// ─────────────────────────────────────────────────────
function CreateEasybillCustomerForm({ inquiry, onCreated, buttonLabel = 'Kunde in Easybill anlegen' }) {
  const [open, setOpen] = useState(false);
  const [extracted, setExtracted] = useState(inquiry?.extractedCustomer || null);
  const [extracting, setExtracting] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', company_name: '',
    email: '', phone_1: '',
    street: '', zip_code: '', city: '', country: 'DE',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Wenn `extracted` vorliegt, ins Formular spiegeln (auch wenn nur Teilfelder gefüllt sind)
  const applyExtracted = useCallback((src) => {
    if (!src) return;
    setForm({
      first_name: src.first_name || '',
      last_name: src.last_name || '',
      company_name: src.company_name || '',
      email: src.email || '',
      phone_1: src.phone_1 || '',
      street: src.street || '',
      zip_code: src.zip_code || '',
      city: src.city || '',
      country: src.country || 'DE',
    });
  }, []);

  const runExtraction = async () => {
    setExtracting(true); setErrMsg(null);
    try {
      const r = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/extract-customer`, { method: 'POST' });
      if (r.ok) {
        const d = await r.json();
        setExtracted(d);
        applyExtracted(d);
      } else {
        const d = await r.json().catch(() => ({}));
        setErrMsg(d.detail || 'Extraktion fehlgeschlagen');
      }
    } finally { setExtracting(false); }
  };

  // Beim Öffnen des Formulars: vorhandene extractedCustomer-Daten ins Formular
  // spiegeln; falls noch keine vorhanden → einmalig Extraktion auslösen.
  useEffect(() => {
    if (!open) return;
    if (extracted) {
      applyExtracted(extracted);
    } else {
      runExtraction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    setSubmitting(true); setErrMsg(null);
    try {
      const res = await authFetch(
        `/api/mail-inbound/inquiries/${inquiry.id}/easybill-create-customer`,
        {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Anlage fehlgeschlagen');
      }
      setOpen(false);
      onCreated?.();
    } catch (e) {
      setErrMsg(e.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <div className="mt-3">
        <Button size="sm" onClick={() => setOpen(true)}
                className="h-7 text-[11px] bg-[#9ec043] hover:bg-[#8ab038] text-white"
                data-testid="btn-create-easybill-customer">
          <Plus className="w-3 h-3 mr-1" /> {buttonLabel}
        </Button>
      </div>
    );
  }

  const inputCls = 'text-[12px] px-2 py-1.5 rounded border border-gray-200 bg-white focus:outline-none focus:ring-1 focus:ring-[#9ec043]/40 w-full';

  return (
    <div className="mt-3 border-t border-gray-100 pt-3" data-testid="create-easybill-customer-form">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <p className="text-[11px] text-gray-500 flex-1">
          Werte aus der Mail vorausgefüllt — bitte prüfen und ggf. korrigieren.
          Adresse darf leer bleiben (in Easybill ergänzbar).
        </p>
        <Button size="sm" variant="outline" onClick={runExtraction}
                disabled={extracting} className="h-6 text-[10px]"
                data-testid="ec-rerun-extract"
                title="Mail-Body erneut analysieren und Felder neu vorschlagen">
          <RefreshCw className={`w-3 h-3 mr-1 ${extracting ? 'animate-spin' : ''}`} />
          {extracting ? 'Analysiere…' : 'Neu extrahieren'}
        </Button>
      </div>
      {extracted && (
        <div className="text-[10px] text-gray-500 mb-2 flex items-center gap-2 flex-wrap">
          <span className={`px-1.5 py-0.5 rounded ${
            extracted.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
            extracted.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
            'bg-red-100 text-red-700'
          }`}>
            KI-Vertrauen: {extracted.confidence || 'low'}
          </span>
          {extracted.isForwarded && (
            <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
              Weitergeleitete Mail erkannt
            </span>
          )}
          {extracted.rationale && (
            <span className="text-gray-400 italic">{extracted.rationale}</span>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-[10px] uppercase tracking-wide text-gray-500">Vorname</span>
          <input className={inputCls} value={form.first_name}
                 onChange={(e) => set('first_name', e.target.value)}
                 data-testid="ec-first-name" />
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wide text-gray-500">Nachname</span>
          <input className={inputCls} value={form.last_name}
                 onChange={(e) => set('last_name', e.target.value)}
                 data-testid="ec-last-name" />
        </div>
        <div className="col-span-2">
          <span className="text-[10px] uppercase tracking-wide text-gray-500">Firma (optional)</span>
          <input className={inputCls} value={form.company_name}
                 onChange={(e) => set('company_name', e.target.value)}
                 data-testid="ec-company" />
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wide text-gray-500">E-Mail</span>
          <input className={inputCls} value={form.email}
                 onChange={(e) => set('email', e.target.value)}
                 data-testid="ec-email" />
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wide text-gray-500">Telefon</span>
          <input className={inputCls} value={form.phone_1}
                 onChange={(e) => set('phone_1', e.target.value)}
                 data-testid="ec-phone" />
        </div>
        <div className="col-span-2">
          <span className="text-[10px] uppercase tracking-wide text-gray-500">Straße &amp; Nr. (optional)</span>
          <input className={inputCls} value={form.street}
                 onChange={(e) => set('street', e.target.value)}
                 data-testid="ec-street" />
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wide text-gray-500">PLZ (optional)</span>
          <input className={inputCls} value={form.zip_code}
                 onChange={(e) => set('zip_code', e.target.value)}
                 data-testid="ec-zip" />
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wide text-gray-500">Stadt (optional)</span>
          <input className={inputCls} value={form.city}
                 onChange={(e) => set('city', e.target.value)}
                 data-testid="ec-city" />
        </div>
      </div>
      {errMsg && (
        <p className="text-[11px] text-red-600 mt-2" data-testid="ec-error">{errMsg}</p>
      )}
      <div className="flex justify-end gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={() => setOpen(false)}
                disabled={submitting} className="h-7 text-[11px]"
                data-testid="ec-cancel">Abbrechen</Button>
        <Button size="sm" onClick={submit}
                disabled={submitting || (!form.first_name && !form.last_name && !form.company_name)}
                className="h-7 text-[11px] bg-[#9ec043] hover:bg-[#8ab038] text-white"
                data-testid="ec-submit">
          {submitting ? 'Wird angelegt…' : 'In Easybill anlegen'}
        </Button>
      </div>
    </div>
  );
}

const KIND_LABELS = {
  primary: { label: 'Empfehlung', cls: 'bg-[#9ec043] text-white' },
  module: { label: 'Modul', cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  genlink: { label: 'GenLink', cls: 'bg-orange-100 text-orange-700 border border-orange-200' },
  alternative: { label: 'Alternative', cls: 'bg-blue-100 text-blue-700 border border-blue-200' },
  transport: { label: 'Transport', cls: 'bg-amber-100 text-amber-700 border border-amber-200' },
  standard: { label: 'Position', cls: 'bg-gray-100 text-gray-600 border border-gray-200' },
};

function KindBadge({ kind }) {
  const k = KIND_LABELS[kind] || KIND_LABELS.standard;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${k.cls}`}
          data-testid={`kind-badge-${kind || 'standard'}`}>
      {k.label}
    </span>
  );
}

function CustomerDataForm({ inquiry, onChange }) {
  const cd = inquiry?.customerData || {};
  // Was hat die KI aus der Mail bereits extrahiert?
  const ext = inquiry?.extractedData || {};
  const fromMail = (k) => ext[k] !== undefined && ext[k] !== null && ext[k] !== '';

  const [draft, setDraft] = useState({
    wantedKwh: cd.wantedKwh ?? inquiry?.wantedKwh ?? '',
    annualKwh: cd.annualKwh ?? '',
    pvKwp: cd.pvKwp ?? ext.pvKwp ?? '',
    buildingType: cd.buildingType ?? '',
    backup: cd.backup ?? false,
    gridReplacement: cd.gridReplacement ?? false,
    generator: cd.generator ?? false,
    generatorPhases: cd.generatorPhases ?? '',
    eastWest: cd.eastWest ?? false,
    hasExistingPv: cd.hasExistingPv ?? !!inquiry?.externalPvInverter,
    specialComponents: cd.specialComponents ?? '',
    customerNotes: cd.customerNotes ?? '',
  });
  const [saving, setSaving] = useState(false);

  // Sync Draft, falls Inquiry sich extern ändert
  useEffect(() => {
    const cd2 = inquiry?.customerData || {};
    setDraft({
      wantedKwh: cd2.wantedKwh ?? inquiry?.wantedKwh ?? '',
      annualKwh: cd2.annualKwh ?? '',
      pvKwp: cd2.pvKwp ?? ext.pvKwp ?? '',
      buildingType: cd2.buildingType ?? '',
      backup: cd2.backup ?? false,
      gridReplacement: cd2.gridReplacement ?? false,
      generator: cd2.generator ?? false,
      generatorPhases: cd2.generatorPhases ?? '',
      eastWest: cd2.eastWest ?? false,
      hasExistingPv: cd2.hasExistingPv ?? !!inquiry?.externalPvInverter,
      specialComponents: cd2.specialComponents ?? '',
      customerNotes: cd2.customerNotes ?? '',
    });
  }, [inquiry?.id, inquiry?.customerData, inquiry?.wantedKwh, inquiry?.externalPvInverter, ext.pvKwp]);  // eslint-disable-line react-hooks/exhaustive-deps

  // „Fehlt"-Flag: Pflichtfeld + Wert leer + nicht aus Mail extrahiert
  const isMissing = (key, required = true) => {
    if (!required) return false;
    const v = draft[key];
    if (typeof v === 'boolean') return false;     // Booleans haben immer Wert
    return v === '' || v === null || v === undefined;
  };

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {};
      const NUMERIC_KEYS = ['wantedKwh', 'annualKwh', 'pvKwp', 'generatorPhases'];
      const TEXT_KEYS = ['buildingType', 'specialComponents', 'customerNotes'];
      Object.entries(draft).forEach(([k, v]) => {
        if (NUMERIC_KEYS.includes(k)) {
          // Leere Strings explizit als null senden, damit das Feld
          // serverseitig wirklich gelöscht wird.
          payload[k] = (v === '' || v === null) ? null : (parseFloat(v) || 0);
        } else if (TEXT_KEYS.includes(k)) {
          // Text-Felder: leer = explizit löschen (null senden)
          payload[k] = (v === '' || v === null) ? null : v;
        } else {
          // Booleans / Sonstiges
          payload[k] = v;
        }
      });
      const r = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/customer-data`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (r.ok) onChange?.();
    } finally { setSaving(false); }
  };

  // GenLink-Empfehlung
  const showGenLink = !!draft.generator;
  const genLinkHint = showGenLink
    ? (draft.generatorPhases === 3 || draft.generatorPhases === '3'
        ? 'Hinweis: GenLink hat einphasigen Eingang — bei dreiphasigem Generator wird nur eine Phase zum Laden genutzt (ca. ⅓ der Nennleistung).'
        : draft.generatorPhases === 1 || draft.generatorPhases === '1'
          ? 'GenLink passt direkt: einphasiger Generator → einphasiger Eingang, volle Leistung nutzbar.'
          : 'Bitte Phasen-Anzahl des Generators erfragen — entscheidet über GenLink-Konfiguration.')
    : null;

  // Visual-Helper
  const yellow = 'border-amber-300 bg-amber-50 focus:ring-amber-200';
  const normal = 'border-gray-200 bg-white focus:ring-gray-200';
  const inputCls = (key, required = true) =>
    `text-[12px] px-2 py-1.5 rounded border focus:outline-none focus:ring-1 ${
      isMissing(key, required) ? yellow : (fromMail(key) ? 'border-emerald-300 bg-emerald-50' : normal)
    }`;
  const Label = ({ children, missing, fromMailFlag }) => (
    <span className="text-[10px] uppercase tracking-wide text-gray-500 flex items-center gap-1">
      {children}
      {missing && <span className="text-amber-700 font-bold">⚠️ fehlt</span>}
      {fromMailFlag && <span className="text-emerald-700">✓ aus Mail</span>}
    </span>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4" data-testid="customer-data-form">
      <h3 className="text-sm font-semibold text-[#32373c] mb-2 flex items-center gap-2">
        <UserIcon className="w-4 h-4 text-[#9ec043]" />
        Strukturierte Anfrage-Daten
      </h3>
      <p className="text-[11px] text-gray-500 mb-3">
        Gelb = fehlt für eine vollständige Auslegung — beim Telefonat abfragen.
        Grün = aus der Mail automatisch übernommen.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div className="flex flex-col gap-1">
          <Label missing={isMissing('wantedKwh')}>Gewünschte Kapazität (kWh)</Label>
          <input type="number" step="0.5" value={draft.wantedKwh} onChange={(e) => set('wantedKwh', e.target.value)}
                 className={inputCls('wantedKwh')} data-testid="cd-wantedKwh" />
        </div>
        <div className="flex flex-col gap-1">
          <Label missing={isMissing('annualKwh')}>Jahresverbrauch (kWh/Jahr)</Label>
          <input type="number" step="500" value={draft.annualKwh} onChange={(e) => set('annualKwh', e.target.value)}
                 className={inputCls('annualKwh')} data-testid="cd-annualKwh" />
        </div>
        <div className="flex flex-col gap-1">
          <Label missing={isMissing('pvKwp')} fromMailFlag={fromMail('pvKwp')}>PV-Leistung (kWp)</Label>
          <input type="number" step="0.1" value={draft.pvKwp} onChange={(e) => set('pvKwp', e.target.value)}
                 className={inputCls('pvKwp')} data-testid="cd-pvKwp" />
        </div>
        <div className="flex flex-col gap-1">
          <Label missing={isMissing('buildingType')}>Gebäudetyp</Label>
          <select value={draft.buildingType} onChange={(e) => set('buildingType', e.target.value)}
                  className={inputCls('buildingType')} data-testid="cd-buildingType">
            <option value="">— wählen —</option>
            <option value="efh">Einfamilienhaus</option>
            <option value="villa">Villa / Altbau</option>
            <option value="mfh">Mehrfamilienhaus</option>
            <option value="commercial">Gewerbe / Werkstatt</option>
            <option value="agri">Landwirtschaft</option>
          </select>
        </div>
        <div className="flex flex-col gap-1 md:col-span-2">
          <Label missing={false}>Sonderkomponenten (Wärmepumpe, Wallbox kW, Pool, …)</Label>
          <input type="text" value={draft.specialComponents} onChange={(e) => set('specialComponents', e.target.value)}
                 className={inputCls('specialComponents', false)} placeholder="z.B. Wärmepumpe 12 kW, Wallbox 11 kW"
                 data-testid="cd-special" />
        </div>
      </div>

      {/* Funktionen */}
      <div className="bg-gray-50 rounded-md p-3 mb-3">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-2">Gewünschte Funktionen</div>
        <div className="flex flex-wrap gap-3 text-[12px]">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={draft.backup} onChange={(e) => set('backup', e.target.checked)}
                   data-testid="cd-backup" />
            Notstrom
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={draft.gridReplacement} onChange={(e) => set('gridReplacement', e.target.checked)}
                   data-testid="cd-gridReplacement" />
            Netzersatz / Inselbetrieb
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={draft.generator} onChange={(e) => set('generator', e.target.checked)}
                   data-testid="cd-generator" />
            Generator-Einbindung (GenLink)
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={draft.eastWest} onChange={(e) => set('eastWest', e.target.checked)}
                   data-testid="cd-eastWest" />
            Ost/West-Dach
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={draft.hasExistingPv} onChange={(e) => set('hasExistingPv', e.target.checked)}
                   data-testid="cd-existingPv" />
            Bestehende PV-Anlage
          </label>
        </div>
        {showGenLink && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px] text-gray-600">Generator-Phasen:</span>
              <select value={draft.generatorPhases} onChange={(e) => set('generatorPhases', parseInt(e.target.value, 10) || '')}
                      className={`text-[12px] px-1.5 py-0.5 rounded border ${
                        !draft.generatorPhases ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'
                      }`}
                      data-testid="cd-generatorPhases">
                <option value="">— wählen —</option>
                <option value="1">1-phasig</option>
                <option value="3">3-phasig</option>
              </select>
              {!draft.generatorPhases && <span className="text-[10px] text-amber-700">⚠️ am Telefon erfragen</span>}
            </div>
            {genLinkHint && (
              <div className="text-[11px] bg-sky-50 border border-sky-200 rounded px-2 py-1 text-sky-900 mt-1"
                   data-testid="genlink-hint">
                {genLinkHint}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1 mb-3">
        <Label missing={false}>Anmerkungen</Label>
        <textarea value={draft.customerNotes} onChange={(e) => set('customerNotes', e.target.value)}
                  rows={2} placeholder="Sonstiges aus dem Telefonat / der Mail …"
                  className={inputCls('customerNotes', false)} data-testid="cd-notes" />
      </div>

      <div className="flex justify-end">
        <button onClick={submit} disabled={saving}
                className="px-3 py-1.5 text-xs font-semibold rounded bg-[#9ec043] text-white hover:bg-[#8ab035] disabled:opacity-40"
                data-testid="cd-save">
          {saving ? 'Speichert…' : 'Daten speichern'}
        </button>
      </div>
    </div>
  );
}


function NotesAndThreadPanel({ inquiry, onChange }) {
  const [thread, setThread] = useState(null);
  const [showThread, setShowThread] = useState(false);
  const [text, setText] = useState('');
  const [kind, setKind] = useState('phone');
  const [busy, setBusy] = useState(false);

  const notes = inquiry?.notes || [];

  const loadThread = useCallback(async () => {
    if (!inquiry?.id) return;
    const r = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/thread`);
    if (r.ok) setThread(await r.json());
  }, [inquiry?.id]);

  useEffect(() => {
    if (showThread && !thread) loadThread();
  }, [showThread, thread, loadThread]);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim(), kind }),
      });
      if (r.ok) { setText(''); onChange?.(); }
    } finally { setBusy(false); }
  };

  const remove = async (noteId) => {
    if (!window.confirm('Notiz löschen?')) return;
    const r = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/notes/${noteId}`, { method: 'DELETE' });
    if (r.ok) onChange?.();
  };

  // Verknüpfungen — vorhandene Felder
  const linkedOffer = inquiry?.offerId || inquiry?.relatedOfferId;
  const linkedOrder = inquiry?.orderId || inquiry?.relatedOrderId;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4" data-testid="notes-thread-panel">
      {/* Verknüpfungen */}
      {(linkedOffer || linkedOrder) && (
        <div className="mb-3 text-[11px] flex flex-wrap gap-2">
          {linkedOffer && (
            <a href={`/dashboard/angebote/${linkedOffer}`}
               className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 hover:bg-emerald-100"
               data-testid="link-offer">
              <FileText className="w-3 h-3" /> Verknüpftes Angebot
            </a>
          )}
          {linkedOrder && (
            <a href={`/dashboard/auftraege/${linkedOrder}`}
               className="inline-flex items-center gap-1 px-2 py-1 rounded bg-sky-50 border border-sky-200 text-sky-800 hover:bg-sky-100"
               data-testid="link-order">
              <Package className="w-3 h-3" /> Verknüpfter Auftrag
            </a>
          )}
        </div>
      )}

      {/* Verknüpfte Aufgaben (kompakte Übersicht mit Status-Badges) */}
      {inquiry?.id && (
        <div className="mb-4 pb-3 border-b border-gray-100">
          <LinkedTasksPanel sourceId={inquiry.id} title="Aufgaben zur Anfrage" />
        </div>
      )}

      {/* Notizen */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-[#32373c] mb-2 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#9ec043]" />
          Notizen / Telefonate ({notes.length})
        </h3>
        {notes.length > 0 && (
          <div className="space-y-2 mb-3">
            {notes.map(n => (
              <div key={n.id} className="flex items-start gap-2 bg-gray-50 border border-gray-100 rounded-md p-2"
                   data-testid={`note-${n.id}`}>
                <div className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${
                  n.kind === 'phone' ? 'bg-sky-100 text-sky-700'
                  : n.kind === 'internal' ? 'bg-amber-100 text-amber-700'
                  : 'bg-gray-200 text-gray-700'
                }`}>
                  {n.kind === 'phone' ? '📞 Tel' : n.kind === 'internal' ? '🔒 Intern' : 'Notiz'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] text-gray-700 whitespace-pre-wrap">{n.text}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    {n.authorName} · {fmtDate(n.createdAt)}
                  </div>
                </div>
                <button onClick={() => remove(n.id)}
                        className="text-rose-400 hover:text-rose-600 shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <select value={kind} onChange={(e) => setKind(e.target.value)}
                    className="text-[11px] px-1.5 py-1 rounded border border-gray-200 bg-white"
                    data-testid="note-kind">
              <option value="phone">📞 Telefonat</option>
              <option value="note">Notiz</option>
              <option value="internal">🔒 Intern</option>
            </select>
            <span className="text-[10px] text-gray-400">Festhalten was besprochen wurde — Kapazität, Jahresverbrauch, kWp, Notstrom-Wunsch, Sonderkomponenten</span>
          </div>
          <textarea value={text} onChange={(e) => setText(e.target.value)}
                    placeholder="Telefonat-Inhalt eintragen…"
                    rows={3}
                    className="text-[12px] px-2 py-1.5 rounded border border-gray-200 bg-white"
                    data-testid="note-text" />
          <button onClick={submit} disabled={busy || !text.trim()}
                  className="self-end px-3 py-1.5 text-xs font-semibold rounded bg-[#9ec043] text-white hover:bg-[#8ab035] disabled:opacity-40 inline-flex items-center gap-1.5"
                  data-testid="note-submit">
            <Send className="w-3.5 h-3.5" /> Notiz hinzufügen
          </button>
        </div>
      </div>

      {/* Mail-Thread */}
      <div>
        <button onClick={() => setShowThread(s => !s)}
                className="text-[11px] text-gray-500 hover:text-[#9ec043] inline-flex items-center gap-1"
                data-testid="toggle-thread">
          <Mail className="w-3 h-3" />
          {showThread ? 'Mail-Verlauf ausblenden' : `Mail-Verlauf einblenden${thread ? ` (${thread.count})` : ''}`}
        </button>
        {showThread && thread && (
          <div className="mt-2 space-y-1 border-l-2 border-gray-100 pl-3">
            {thread.items.map((m, i) => (
              <div key={m.id || i} className={`text-[11px] ${m.id === inquiry.id ? 'font-semibold text-[#32373c]' : 'text-gray-500'}`}>
                {fmtDate(m.receivedAt || m.createdAt)} — {m.customerName || m.customerEmail || '—'}
                {m.id === inquiry.id && <span className="ml-1 text-[#9ec043]">(diese Mail)</span>}
                <div className="text-[10px] text-gray-400 ml-2">{(m.subject || '').slice(0, 80)}</div>
              </div>
            ))}
            {thread.items.length === 1 && <div className="text-[10px] text-gray-400 italic">Keine weiteren Mails im Thread gefunden.</div>}
          </div>
        )}
      </div>

      {/* Gebündelte Ergänzungs-Mails (z.B. „Ergänzung zu Bestellung B26PL010")
          werden direkt in dieser Anfrage angezeigt — derselbe Vorgang, eine
          gemeinsame Akte. */}
      {Array.isArray(inquiry?.mailThread) && inquiry.mailThread.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100" data-testid="bundled-supplements">
          <h4 className="text-sm font-semibold text-[#32373c] mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#9ec043]" />
            Gebündelte Mails ({inquiry.mailThread.length})
            {inquiry.externalOrderId && (
              <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-[#9ec043]/10 text-[#7a9234]">
                Order-ID {inquiry.externalOrderId}
              </span>
            )}
          </h4>
          <p className="text-[10px] text-gray-400 mb-2">
            Ergänzungs-Mails zur selben Bestellung werden automatisch in dieser Anfrage zusammengefasst, statt eine zweite Anfrage anzulegen.
          </p>
          <div className="space-y-2">
            {inquiry.mailThread.map((m, idx) => (
              <details key={m.id || idx} className="bg-amber-50/40 border border-amber-100 rounded-md text-xs"
                       data-testid={`bundle-mail-${idx}`}>
                <summary className="cursor-pointer p-2 flex items-center gap-2 select-none">
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 shrink-0">
                    {m.isSupplement ? 'Ergänzung' : 'Folge-Mail'}
                  </span>
                  <span className="text-gray-700 truncate flex-1">{m.subject || '(ohne Betreff)'}</span>
                  <span className="text-[10px] text-gray-400 shrink-0">{fmtDate(m.receivedAt || m.addedAt)}</span>
                </summary>
                <div className="p-3 border-t border-amber-100 space-y-1 bg-white">
                  <div className="text-[10px] text-gray-400 flex items-center gap-2">
                    <span>Von: {m.fromName || ''} {m.fromEmail ? `<${m.fromEmail}>` : ''}</span>
                    <button type="button"
                            onClick={async () => {
                              if (!window.confirm('Diese Mail aus dem Bundle herauslösen und als eigene Anfrage anzeigen?')) return;
                              const r = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/unbundle/${m.id}`, { method: 'POST' });
                              if (r.ok) onChange?.();
                              else {
                                const d = await r.json().catch(() => ({}));
                                alert(d.detail || 'Entbündeln fehlgeschlagen');
                              }
                            }}
                            className="ml-auto text-[10px] text-amber-700 hover:text-amber-900 underline"
                            data-testid={`btn-unbundle-${idx}`}
                            title="Diese Mail wieder als eigene Anfrage anzeigen">
                      Entbündeln
                    </button>
                  </div>
                  {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                    <div className="text-[10px] text-gray-500">
                      Anhänge: {m.attachments.map(a => a.name).filter(Boolean).join(', ')}
                    </div>
                  )}
                  {m.bodyHtml ? (
                    <div className="text-xs text-gray-700 prose-sm" dangerouslySetInnerHTML={{ __html: m.bodyHtml }} />
                  ) : (
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans">{m.bodyText}</pre>
                  )}
                </div>
              </details>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function OfferDraftPanel({ inquiry, onCreated, onAfterOrder, refreshTick = 0 }) {
  const [items, setItems] = useState([emptyItem()]);
  const [title, setTitle] = useState(inquiry.subject?.replace(/^(re|fw|fwd|aw|wg):\s*/i, '') || '');
  const [searchQ, setSearchQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  // 25.08.2026: Angebot ODER direkter Auftrag — User-Wahl im Radio-Button
  const [docType, setDocType] = useState('OFFER');   // 'OFFER' | 'CHARGE'
  const [createdOffer, setCreatedOffer] = useState(
    (inquiry.offers && inquiry.offers[inquiry.offers.length - 1]) || null,
  );

  // 04.02.2026 (kritischer Bugfix, Defense-in-Depth): Beim Wechsel der
  // Anfrage MÜSSEN alle Panel-States (title, items, errMsg, docType)
  // resettet werden, damit auf keinen Fall Daten aus einer vorherigen
  // Anfrage in einen neuen Easybill-Auftrag durchsickern. Der `key`-Prop
  // in der Elternkomponente erledigt das schon, dieser Effect ist die
  // Sicherheitsnetz-Schicht falls das `key`-Muster mal aus Versehen
  // entfernt wird.
  useEffect(() => {
    setTitle(inquiry.subject?.replace(/^(re|fw|fwd|aw|wg):\s*/i, '') || '');
    setItems([emptyItem()]);
    setErrMsg(null);
    setDocType('OFFER');
    setCreatedOffer((inquiry.offers && inquiry.offers[inquiry.offers.length - 1]) || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inquiry.id]);

  // Positionen die wirklich ins Angebot wandern (Mengen > 0). Alternativen
  // mit Menge 0 werden zur Info angezeigt, aber NICHT an Easybill gesendet.
  const billableItems = items.filter(i => Number(i.quantity) > 0);
  const canCreate = !!inquiry.easybillCustomer?.id && billableItems.length > 0 &&
                    billableItems.every(i => i.description);

  useEffect(() => {
    setCreatedOffer((inquiry.offers && inquiry.offers[inquiry.offers.length - 1]) || null);
  }, [inquiry.id, inquiry.offers]);

  // Auto-prefill: holt Primary + Modules + GenLink + Alternativen + Transport
  // direkt vom Backend (das den Easybill-Artikelkatalog für Preise konsultiert).
  // - Beim Öffnen der Anfrage: einmal initial.
  // - Nach jedem CustomerData-Save (refreshTick ändert sich): überschreibt die
  //   Positionen mit der neu berechneten Empfehlung. Bereits erstellte Angebote
  //   werden NICHT überschrieben.
  useEffect(() => {
    if (createdOffer) return;
    if (prefilling) return;
    // Beim allerersten Mal nur, wenn noch nichts eingegeben wurde
    if (refreshTick === 0 && (items.length !== 1 || items[0].description)) return;
    if (!inquiry.productRecommendation && !(inquiry.productAlternatives?.length)) return;
    setPrefilling(true);
    (async () => {
      try {
        const res = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/offer-prefill`);
        if (res.ok) {
          const d = await res.json();
          if (d.items?.length) {
            setItems(d.items);
            return;
          }
        }
        // Fallback: mindestens Transport + Primary als Blanko
        const fallback = [];
        if (inquiry.productRecommendation) {
          fallback.push({ ...emptyItem(), kind: 'primary',
                          description: inquiry.productRecommendation });
        }
        fallback.push({ ...emptyItem(), kind: 'transport',
                        description: 'Transport & Lieferung', unit: 'Pauschal',
                        single_price_net: 0 });
        setItems(fallback.length ? fallback : [emptyItem()]);
      } catch { /* silent */ }
      finally { setPrefilling(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inquiry.id, refreshTick]);

  const searchArticles = async () => {
    if (searchQ.trim().length < 2) return;
    setSearching(true);
    // 27.02.2026: Preisgruppe des zugeordneten Easybill-Kunden mitgeben,
    // sonst liefert der Backend-Endpoint immer sale_price (Preisgruppe 1),
    // auch wenn der Kunde SALEPRICE2..10 zugewiesen ist.
    const level = (inquiry.easybillCustomer?.salePriceLevel || '').trim();
    const qs = new URLSearchParams({ q: searchQ });
    if (level && level !== 'SALEPRICE' && level !== 'SALEPRICE1') {
      qs.set('sale_price_level', level);
    }
    const res = await authFetch(`/api/integrations/easybill/articles?${qs.toString()}`);
    setSearching(false);
    if (res.ok) {
      const d = await res.json();
      setResults(d.items || []);
    } else setResults([]);
  };

  const addFromCatalogue = (a) => {
    setItems(prev => [...prev, {
      kind: 'standard',
      number: a.number || '',
      description: a.description || '',
      quantity: 1,
      single_price_net: Number(a.single_price_net || a.single_price || 0),
      vat_percent: Number(a.vat_percent || 19),
      unit: a.unit || 'Stück',
    }]);
    setResults([]); setSearchQ('');
  };

  const addBlank = () => setItems(prev => [...prev, emptyItem()]);
  const updateItem = (idx, key, val) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: val } : it));
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const createOffer = async () => {
    setCreating(true); setErrMsg(null);
    // Nur Positionen mit Menge > 0 ins Angebot übernehmen — Alternativen mit
    // Menge 0 sind UI-Info und gehören nicht in Easybill.
    const body = {
      items: items
        .filter(it => Number(it.quantity) > 0)
        .map(it => ({
          number: it.number || null,
          description: it.description,
          quantity: Number(it.quantity) || 1,
          single_price_net: Number(it.single_price_net) || 0,
          vat_percent: Number(it.vat_percent) || 19,
          unit: it.unit || 'Stück',
        })),
      title,
      doc_type: docType,
    };
    const res = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/create-offer`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setCreating(false);
    if (res.ok) {
      const d = await res.json();
      setCreatedOffer(d);
      onCreated?.();
    } else {
      const d = await res.json().catch(() => ({}));
      setErrMsg(d.detail || 'Angebot konnte nicht erstellt werden');
    }
  };

  const netTotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.single_price_net) || 0), 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4" data-testid="offer-panel">
      <h3 className="text-sm font-semibold text-[#32373c] mb-3 flex items-center gap-2">
        <FileText className="w-4 h-4 text-[#9ec043]" /> Angebot erstellen
      </h3>

      {createdOffer?.documentId && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3 flex items-center justify-between flex-wrap gap-2" data-testid="offer-created">
          <div className="text-xs text-green-800">
            <strong>Angebot #{createdOffer.documentNumber || createdOffer.documentId}</strong> in Easybill angelegt
            {createdOffer.grandTotalGross != null && ` · ${Number(createdOffer.grandTotalGross).toFixed(2)} € brutto`}
            {createdOffer.createdAt && <span className="text-green-600/70"> · {fmtDate(createdOffer.createdAt)}</span>}
          </div>
          <div className="flex items-center gap-3">
            <a href={createdOffer.easybillUrl || `https://app.easybill.de/documents?type=OFFER${createdOffer.documentNumber ? `&search=${encodeURIComponent(createdOffer.documentNumber)}` : ''}`}
               target="_blank" rel="noreferrer"
               className="text-[11px] text-green-700 hover:text-green-900 flex items-center gap-1 underline"
               data-testid="offer-open-link"
               title="Öffnet die Easybill-Dokumentenliste, gefiltert nach der Angebots-Nummer">
              <ExternalLink className="w-3 h-3" /> In Easybill öffnen
            </a>
            {!inquiry.order?.documentId && (
              <Button onClick={async () => {
                if (!window.confirm('Soll aus dem Angebot eine Auftragsbestätigung in Easybill erstellt und direkt im Dashboard übernommen werden?')) return;
                const r = await authFetch('/api/integrations/easybill/orders/from-offer', {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ offerDocumentId: createdOffer.documentId, inquiryId: inquiry.id }),
                });
                if (r.ok) { const d = await r.json(); alert(`Auftrag #${d.easybillDocumentNumber} erstellt.`); if (typeof onAfterOrder === 'function') onAfterOrder(); }
                else { const d = await r.json().catch(() => ({})); alert(d.detail || 'Auftrag konnte nicht erstellt werden'); }
              }}
              className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-xs h-7"
              data-testid="btn-create-order-from-offer">
                Auftragsbestätigung erstellen
              </Button>
            )}
            {inquiry.order?.documentId && (
              <span className="text-[11px] text-green-700 font-medium" data-testid="order-badge">
                ✓ Auftrag #{inquiry.order.documentNumber || inquiry.order.documentId}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="mb-3">
        <Label className="text-xs text-gray-500">Artikel aus Easybill-Katalog suchen</Label>
        <div className="flex gap-2 mt-1">
          <Input value={searchQ} onChange={e => setSearchQ(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchArticles())}
                 placeholder="z. B. HS 5 PV duo" className="h-9 text-sm"
                 data-testid="article-search-input" />
          <Button onClick={searchArticles} disabled={searching || searchQ.trim().length < 2}
                  variant="outline" className="h-9 text-xs" data-testid="btn-article-search">
            {searching ? 'Suche…' : 'Suchen'}
          </Button>
        </div>
        {results.length > 0 && (
          <div className="border border-gray-200 rounded-md mt-2 max-h-64 overflow-y-auto">
            {results.map(a => (
              <button key={a.id} onClick={() => addFromCatalogue(a)}
                      className={`w-full text-left p-2 hover:bg-gray-50 border-b border-gray-100 last:border-0 ${a.archived ? 'opacity-60' : ''}`}
                      data-testid={`article-result-${a.id}`}>
                <div className="flex items-center gap-2">
                  <div className="text-xs font-medium text-[#32373c] truncate flex-1">{a.descriptionPreview || a.description}</div>
                  <span className="text-[11px] text-[#32373c] font-semibold shrink-0">
                    {Number(a.single_price_net || 0).toFixed(2)} €
                  </span>
                </div>
                <div className="text-[10px] text-gray-400 flex items-center gap-2 mt-0.5">
                  <span>{a.number || '—'}</span>
                  {a.groupName && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{a.groupName}</span>}
                  {a.archived && <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600">archiviert</span>}
                  {a.stockCount != null && a.stockCount > 0 && (
                    <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700">Lager: {a.stockCount}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2 mb-3">
        {items.length > 0 && (
          <div className="grid grid-cols-12 gap-1.5 items-center text-[10px] uppercase tracking-wide text-gray-400 font-semibold px-0.5">
            <div className="col-span-2">Typ</div>
            <div className="col-span-1">SKU</div>
            <div className="col-span-4">Beschreibung</div>
            <div className="col-span-1">Menge</div>
            <div className="col-span-2">€ netto</div>
            <div className="col-span-1">USt %</div>
            <div className="col-span-1" />
          </div>
        )}
        {items.map((it, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-1.5 items-start" data-testid={`offer-item-${idx}`}>
            <div className="col-span-2 pt-1.5">
              <KindBadge kind={it.kind} />
              {it.kind === 'alternative' && it.note && (
                <div className="text-[9px] text-gray-400 mt-1 leading-snug">{it.note}</div>
              )}
              {it.kind === 'module' && it.note && (
                <div className="text-[9px] text-emerald-600 mt-1 leading-snug">{it.note}</div>
              )}
              {it.kind === 'transport' && (
                <div className="text-[9px] text-amber-600 mt-1 leading-snug">Preis festlegen</div>
              )}
            </div>
            <Input value={it.number} onChange={e => updateItem(idx, 'number', e.target.value)}
                   placeholder="SKU" className="col-span-1 h-8 text-xs" />
            <textarea value={it.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                   placeholder="Beschreibung (Zeilenumbrüche bleiben erhalten)"
                   rows={it.description && it.description.length > 80 ? 3 : 1}
                   className="col-span-4 min-h-8 text-xs rounded-md border border-gray-300 bg-white px-2 py-1.5 leading-relaxed focus:outline-none focus:border-[#9ec043] resize-y"
                   data-testid={`offer-desc-${idx}`} />
            <Input type="number" step="0.01" value={it.quantity}
                   onChange={e => updateItem(idx, 'quantity', e.target.value)}
                   placeholder="Menge" className="col-span-1 h-8 text-xs" />
            <Input type="number" step="0.01" value={it.single_price_net}
                   onChange={e => updateItem(idx, 'single_price_net', e.target.value)}
                   placeholder="€ netto" className="col-span-2 h-8 text-xs"
                   data-testid={`offer-price-${idx}`} />
            <Input type="number" step="1" value={it.vat_percent}
                   onChange={e => updateItem(idx, 'vat_percent', e.target.value)}
                   placeholder="USt %" className="col-span-1 h-8 text-xs" />
            <button onClick={() => removeItem(idx)} className="col-span-1 p-1 text-gray-400 hover:text-red-500 self-center"
                    data-testid={`btn-remove-item-${idx}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <Button onClick={addBlank} size="sm" variant="outline" className="h-7 text-[11px]" data-testid="btn-add-item">
          <Plus className="w-3 h-3 mr-1" /> Position hinzufügen
        </Button>
        <div className="text-xs text-gray-600">
          Netto-Summe: <strong className="text-[#32373c]">{netTotal.toFixed(2)} €</strong>
        </div>
      </div>

      <div className="mb-3" data-testid="offer-doctype">
        <Label className="text-xs text-gray-500">In Easybill anlegen als</Label>
        <div className="flex gap-2 mt-1">
          <label className={`flex-1 border rounded px-3 py-2 cursor-pointer text-xs ${docType === 'OFFER' ? 'border-[#9ec043] bg-[#f8faf2] font-medium text-[#32373c]' : 'border-gray-200 text-gray-500'}`}>
            <input type="radio" name="offer-doctype" className="mr-2" checked={docType === 'OFFER'}
                   onChange={() => setDocType('OFFER')} data-testid="offer-doctype-offer" />
            Angebot (Standard)
          </label>
          <label className={`flex-1 border rounded px-3 py-2 cursor-pointer text-xs ${docType === 'CHARGE' ? 'border-[#9ec043] bg-[#f8faf2] font-medium text-[#32373c]' : 'border-gray-200 text-gray-500'}`}>
            <input type="radio" name="offer-doctype" className="mr-2" checked={docType === 'CHARGE'}
                   onChange={() => setDocType('CHARGE')} data-testid="offer-doctype-charge" />
            Auftrag
          </label>
          <label className={`flex-1 border rounded px-3 py-2 cursor-pointer text-xs ${docType === 'DELIVERY' ? 'border-[#9ec043] bg-[#f8faf2] font-medium text-[#32373c]' : 'border-gray-200 text-gray-500'}`}>
            <input type="radio" name="offer-doctype" className="mr-2" checked={docType === 'DELIVERY'}
                   onChange={() => setDocType('DELIVERY')} data-testid="offer-doctype-delivery" />
            Lieferschein (direkte Bestellung)
          </label>
        </div>
      </div>

      <div className="mb-3">
        <Label className="text-xs text-gray-500">
          {docType === 'DELIVERY' ? 'Lieferschein-Titel'
            : docType === 'CHARGE' ? 'Auftragstitel'
            : 'Angebotstitel'}
        </Label>
        <Input value={title} onChange={e => setTitle(e.target.value)}
               className="h-9 text-sm mt-1" data-testid="offer-title" />
      </div>

      {errMsg && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-2" data-testid="offer-error">
          {errMsg}
        </div>
      )}

      <Button onClick={createOffer} disabled={!canCreate || creating}
              className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm w-full"
              data-testid="btn-create-offer">
        <FileText className="w-3.5 h-3.5 mr-1" />
        {creating ? 'Wird erstellt…' : (
          docType === 'DELIVERY'
            ? (createdOffer ? 'Neuen Lieferschein in Easybill erstellen' : 'Lieferschein in Easybill erstellen')
            : docType === 'CHARGE'
              ? (createdOffer ? 'Neuen Auftrag in Easybill erstellen' : 'Auftrag in Easybill erstellen')
              : (createdOffer ? 'Neues Angebot in Easybill erstellen' : 'Angebot in Easybill erstellen')
        )}
      </Button>

      {!inquiry.easybillCustomer?.id && (
        <p className="text-[10px] text-amber-600 mt-1.5 text-center">
          Kein Easybill-Kunde verknüpft — bitte zuerst „Erneut bei Easybill suchen" oben nutzen.
        </p>
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────
// Manuelle Kundenzuordnung (override bei Fehl-Match)
// ─────────────────────────────────────────────────────
function ManualCustomerAssign({ inquiry, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(null);

  const search = async () => {
    if (q.trim().length < 2) return;
    setSearching(true);
    const res = await authFetch(`/api/integrations/easybill/customers?q=${encodeURIComponent(q)}`);
    setSearching(false);
    if (res.ok) setResults((await res.json()).items || []);
    else setResults([]);
  };

  const assign = async (customerId) => {
    setAssigning(customerId);
    const res = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/assign-customer`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId }),
    });
    setAssigning(null);
    if (res.ok) {
      setOpen(false); setResults([]); setQ('');
      onAssigned?.();
    } else {
      const d = await res.json().catch(() => ({}));
      alert(d.detail || 'Zuordnung fehlgeschlagen');
    }
  };

  if (!open) {
    return (
      <div className="mb-4">
        <button onClick={() => setOpen(true)} data-testid="btn-open-manual-assign"
                className="text-[11px] text-gray-500 hover:text-[#9ec043] underline underline-offset-2 flex items-center gap-1">
          <UserIcon className="w-3 h-3" />
          {inquiry.easybillCustomer
            ? 'Anderen Easybill-Kunden zuordnen'
            : 'Kunden manuell in Easybill suchen und zuordnen'}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4" data-testid="manual-assign-panel">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
          <UserIcon className="w-4 h-4 text-[#9ec043]" /> Kunden manuell zuordnen
        </h4>
        <button onClick={() => { setOpen(false); setResults([]); setQ(''); }}
                className="text-gray-400 hover:text-gray-600" data-testid="btn-close-manual-assign">
          ✕
        </button>
      </div>
      <div className="flex gap-2 mb-2">
        <Input value={q} onChange={e => setQ(e.target.value)}
               onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), search())}
               placeholder="Name, Firma, E-Mail oder Kundennummer"
               className="h-9 text-sm" data-testid="manual-search-input" />
        <Button onClick={search} disabled={searching || q.trim().length < 2}
                variant="outline" className="h-9 text-xs" data-testid="btn-manual-search">
          {searching ? 'Suche…' : 'Suchen'}
        </Button>
      </div>
      {results.length > 0 && (
        <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto" data-testid="manual-results">
          {results.map(c => (
            <button key={c.id} onClick={() => assign(c.id)} disabled={assigning === c.id}
                    className="w-full text-left p-2 hover:bg-gray-50 border-b border-gray-100 last:border-0 disabled:opacity-50"
                    data-testid={`manual-result-${c.id}`}>
              <div className="text-xs font-medium text-[#32373c] flex items-center gap-2">
                <span className="truncate">
                  {c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—'}
                </span>
                {assigning === c.id && <span className="text-[10px] text-gray-400">zuordnend…</span>}
              </div>
              <div className="text-[10px] text-gray-400 flex gap-2 flex-wrap mt-0.5">
                <span>#{c.number}</span>
                {c.company_name && c.last_name && (
                  <span>· {c.first_name} {c.last_name}</span>
                )}
                {(c.emails || [])[0] && <span>· {c.emails[0]}</span>}
                {c.city && <span>· {c.city}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
      {results.length === 0 && !searching && q && (
        <p className="text-[11px] text-gray-400 italic mt-2">Keine Treffer — bitte andere Schreibweise probieren.</p>
      )}
    </div>
  );
}


